import { useMemo, useRef, useState, useEffect } from "react";
import { Button, Divider, Dropdown, Form, Grid, Header, Icon, Label, Message, Modal, Popup, Segment } from "semantic-ui-react";
import bookingRequestFormData from "../../data/bookingRequestFormData.json";
import { ghApi, bookingApi, roomTypeApi } from "../../api/api";
import { API_TO_FE_STATUS, mapSentEmail } from "../../api/mappers";
import StatusBadge from "./StatusBadge";
import BillCard from "./billing/BillCard";
import SendMailModal from "./SendMailModal";
import CopyDetailsButton from "./CopyDetailsButton";
import PrintApplicationFormButton from "./applicationForm/PrintApplicationFormButton";
import TimeInput12h from "../TimeInput12h";
import useVacantRooms from "../../hooks/useVacantRooms";
import { useBooking } from "../../context/BookingContext";
import {
  appendBookingPayload, OFFICIAL_ATTACHMENT_ACCEPT, MAX_ATTACHMENT_SIZE, ATTACHMENT_TOO_LARGE_MSG,
} from "../../utils/multipartForm";
import {
  addRoomPayloadWindows,
  addRoomWindow,
  bookingStayWindow,
  buildRoomWindows,
  extensionWindow,
  splitReusableRooms,
} from "../../utils/stayWindow";
import AddRoomWindowFields, { isAddRoomWindowValid } from "./AddRoomWindowFields";
import {
  toJsonKey,
  checkinTooEarly,
  checkinTooEarlyMsg,
  fmtDate,
  fmtDateAndTime,
  fmtDateTime,
  stayNights,
  hourBlockDuration,
  isTransferHistoryEntry,
  latestTransfer,
  getActualCheckInOut,
  STATUSES_WITH_ASSIGNED_ROOM,
} from "./adminHelpers";

/* Stable defaults — a fresh {} / [] literal per render would re-fire the
   effects that depend on them. */
const ghCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];
const OTHER_PAYMENT_CODE = "OTHER";

/* Reject and Cancel both end a booking, so the difference between them is
   spelled out on the buttons themselves rather than left to be inferred. */
const REJECT_HINT =
  "The guest house declines this request. Only possible while the booking is " +
  "pending or partially confirmed. No mail is sent automatically — you'll be " +
  "prompted to review and send the rejection mail yourself.";
const CANCEL_HINT =
  "The stay is called off. Possible at any point before checkout, and it is " +
  "also what the applicant triggers from their own dashboard. The applicant " +
  "is emailed a cancellation.";

/* Hover hint on an action button — the small ‘i’ is the affordance, the
   whole button is the hover target so the tip is easy to land on. */
function InfoHint({ content, children }) {
  return (
    <Popup
      inverted
      size="tiny"
      position="top center"
      style={{ maxWidth: 260 }}
      trigger={children}
      content={content}
    />
  );
}

/* ── Field cell (always used inside a Grid) ─────────────── */
function F({ label, value, icon, copyable, trailing }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value || value === "—") return;
    navigator.clipboard
      .writeText(String(value))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <Grid.Column>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {icon && <Icon name={icon} />}
        <span>{value || "—"}</span>
        {copyable && value && value !== "—" && (
          <Button size="mini" basic onClick={handleCopy} type="button">
            {copied ? "Copied!" : "Copy"}
          </Button>
        )}
        {trailing}
      </div>
    </Grid.Column>
  );
}

/* datetime-local <-> ISO. The <input type="datetime-local"> value is a
   zone-less "YYYY-MM-DDTHH:MM" in the viewer's local time; the API wants ISO. */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/* ── Inline detail editing: parked, not removed ──────────
   The per-field pencils on Applicant Details, Guest Details and Stay
   Information are hidden for now — the fields render as plain read-only
   rows. Nothing was deleted: flip this to `true` and the whole set comes
   back exactly as it was. Scoped to those three sections only; Payment
   Mode, the per-room check-in/out stamps and the rail's admin field
   overrides are deliberately unaffected. */
const INLINE_DETAIL_EDIT_ENABLED = false;

const EDIT_INPUT_STYLE = { padding: "6px 8px", border: "1px solid rgba(0,0,0,0.2)", borderRadius: 4, fontSize: "0.85rem" };

/* ── Inline one-field editor popup ───────────────────────
   The pencil + click-Popup shared by EditableF and by the per-room
   check-in/out rows. `seed` is the raw current value; on Save it calls
   `onSave(coercedValue)` and closes, or — if onSave throws — keeps the popup
   open showing the error message. */
function FieldEditPopup({ label, type = "text", options, min, rows = 3, warning, seed, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const coerce = (raw) => {
    if (type === "number") return raw === "" || raw == null ? null : Number(raw);
    if (type === "datetime") return localInputToIso(raw);
    if (type === "date" || type === "time") return raw === "" || raw == null ? null : raw;
    if (type === "select") return raw;
    return typeof raw === "string" ? raw.trim() : raw;
  };

  const seedDraft = () =>
    type === "datetime" ? isoToLocalInput(seed) : seed == null ? "" : String(seed);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(coerce(draft));
      setOpen(false);
    } catch (e) {
      setError(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const editor = () => {
    if (type === "select") {
      return (
        <Dropdown selection fluid options={options || []} value={draft} onChange={(_, d) => setDraft(d.value)} />
      );
    }
    if (type === "textarea") {
      return (
        <textarea
          value={draft}
          rows={rows}
          onChange={(e) => setDraft(e.target.value)}
          style={{ ...EDIT_INPUT_STYLE, resize: "vertical" }}
        />
      );
    }
    if (type === "time") {
      return <TimeInput12h value={draft || ""} onChange={(v) => setDraft(v || "")} />;
    }
    if (type === "date") {
      return <input type="date" value={draft || ""} onChange={(e) => setDraft(e.target.value)} style={EDIT_INPUT_STYLE} />;
    }
    if (type === "datetime") {
      return <input type="datetime-local" value={draft || ""} onChange={(e) => setDraft(e.target.value)} style={EDIT_INPUT_STYLE} />;
    }
    return (
      <input
        type={type === "number" ? "number" : "text"}
        value={draft}
        min={type === "number" ? min : undefined}
        onChange={(e) => setDraft(e.target.value)}
        style={EDIT_INPUT_STYLE}
      />
    );
  };

  return (
    <Popup
      on="click"
      open={open}
      onOpen={() => {
        setDraft(seedDraft());
        setError("");
        setOpen(true);
      }}
      onClose={() => !saving && setOpen(false)}
      position="top center"
      trigger={
        <Button size="mini" basic icon type="button" style={{ boxShadow: "none", padding: 4, marginLeft: 4 }}>
          <Icon name="pencil" style={{ margin: 0 }} />
        </Button>
      }
      content={
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220 }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(0,0,0,0.4)" }}>
            {label}
          </div>
          {editor()}
          {type === "datetime" && draft && (
            <Button size="mini" basic type="button" onClick={() => setDraft("")} style={{ alignSelf: "flex-start" }}>
              Clear
            </Button>
          )}
          {warning && (
            <Message warning size="mini" style={{ padding: "6px 8px" }}>
              {warning}
            </Message>
          )}
          {error && (
            <Message negative size="mini" style={{ padding: "6px 8px" }}>
              {error}
            </Message>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <Button size="mini" basic type="button" disabled={saving} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="mini" color="blue" type="button" loading={saving} disabled={saving} onClick={save}>
              Save
            </Button>
          </div>
        </div>
      }
    />
  );
}

/* ── Editable field cell ─────────────────────────────────
   A drop-in replacement for <F> that, when `editable`, adds a small pencil
   opening a `FieldEditPopup`. Gated on the caller's `has_edit_access`; with
   `editable` false it renders exactly like <F>. `value` stays the
   human-readable display; `draftValue` seeds the editor with the raw value. */
function EditableF({
  label,
  value,
  icon,
  copyable,
  editable = false,
  fieldKey,
  type = "text",
  options,
  min,
  rows = 3,
  warning,
  draftValue,
  onSave,
}) {
  const canEdit = editable && Boolean(onSave) && Boolean(fieldKey);
  return (
    <F
      label={label}
      value={value}
      icon={icon}
      copyable={copyable}
      trailing={
        canEdit ? (
          <FieldEditPopup
            label={label}
            type={type}
            options={options}
            min={min}
            rows={rows}
            warning={warning}
            seed={draftValue != null ? draftValue : value}
            onSave={(coerced) => onSave({ [fieldKey]: coerced })}
          />
        ) : null
      }
    />
  );
}

/* ── Rail stat tile (Key Facts) ──────────────────────────
   Bigger, bolder numbers than the flat F fields below — these are the
   handful of facts an admin needs at a glance without reading the whole
   page (nights, rooms, guests, assigned/preferred guest house). */
function Stat({ icon, label, value, sub }) {
  return (
    <div style={{ background: "rgba(20,58,90,0.05)", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ fontSize: "0.64rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", gap: 4 }}>
        <Icon name={icon} style={{ margin: 0 }} />
        {label}
      </div>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#1e2a4a", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: "0.68rem", color: "rgba(0,0,0,0.45)" }}>{sub}</div>}
    </div>
  );
}

/* ── Confirm-on-click button — for a dense row list, a stray click
   shouldn't fire an action, so these go through a small Yes/Cancel
   popup instead of firing immediately. ── */
function ConfirmButton({ icon, text, color, basic, loading, confirmText, onConfirm }) {
  const [open, setOpen] = useState(false);
  return (
    <Popup
      on="click"
      open={open}
      onClose={() => setOpen(false)}
      position="top center"
      trigger={
        <Button size="mini" basic={basic} color={color} loading={loading} disabled={loading} type="button" onClick={() => setOpen(true)}>
          <Icon name={icon} />
          {text}
        </Button>
      }
      content={
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: "0.85rem" }}>{confirmText}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <Button size="mini" basic onClick={() => setOpen(false)} type="button">
              Cancel
            </Button>
            <Button
              size="mini"
              color={color || "blue"}
              onClick={() => {
                setOpen(false);
                onConfirm();
              }}
              type="button"
            >
              Yes
            </Button>
          </div>
        </div>
      }
    />
  );
}

/* ── Per-room guest name — optional, editable any time a room is
   assigned (not gated on check-in state), so it's a plain click-to-edit
   popup rather than a confirm. ── */
function GuestNameField({ value, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  return (
    <Popup
      on="click"
      open={open}
      onOpen={() => {
        setDraft(value || "");
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      position="top center"
      trigger={
        <Button size="mini" basic type="button">
          <Icon name={value ? "user" : "user plus"} />
          {value || "Add guest name"}
        </Button>
      }
      content={
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 200 }}>
          <input
            type="text"
            value={draft}
            placeholder="Guest name"
            onChange={(e) => setDraft(e.target.value)}
            style={{ padding: "6px 8px", border: "1px solid rgba(0,0,0,0.2)", borderRadius: 4, fontSize: "0.85rem" }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <Button size="mini" basic onClick={() => setOpen(false)} type="button" disabled={saving}>
              Cancel
            </Button>
            <Button
              size="mini"
              color="blue"
              loading={saving}
              disabled={saving}
              type="button"
              onClick={async () => {
                setSaving(true);
                try {
                  await onSave(draft.trim());
                  setOpen(false);
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>
      }
    />
  );
}

/* ── Per-room stay windows (confirm / add-room) ───────────
   A room on a multi-room indent need not be held for the whole booking: a
   guest may take it for three days of eight, or arrive two days late. Left
   untouched, a room inherits the booking's own window, which is what every
   assignment did before this existed — so the panel starts collapsed and
   every field starts blank, and a blank field sends nothing. The payload
   builder lives in utils/stayWindow (shared with Front Desk); re-exported
   here for existing importers. */
export { buildRoomWindows };

function RoomWindowsPanel({ rooms, drafts, onChange }) {
  const [open, setOpen] = useState(false);
  if (!rooms.length) return null;

  const set = (roomId, key, value) =>
    onChange({ ...drafts, [roomId]: { ...(drafts[roomId] || {}), [key]: value } });

  return (
    <div style={{ marginTop: 12 }}>
      <Button
        basic
        size="mini"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon name={open ? "chevron down" : "chevron right"} />
        Stagger arrival / departure (optional)
      </Button>
      {open && (
        <Segment secondary style={{ marginTop: 8 }}>
          <p style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.6)", marginTop: 0 }}>
            Leave a room blank to hold it for the booking's whole stay. A room's own
            window may sit inside the booking's or run past it.
          </p>
          {rooms.map((r) => {
            const d = drafts[r.id] || {};
            return (
              <Form key={r.id} style={{ marginBottom: 10 }}>
                <Label basic size="small">
                  <Icon name="door open" />
                  Room <strong>{r.roomNumber}</strong>
                  {r.guestHouseName ? ` (${r.guestHouseName})` : ""}
                </Label>
                {/* Two two-field rows, not one four-field row: with all four
                   in one `widths="equal"` group, a narrow modal squeezes
                   "Departs" and its time past the edge instead of wrapping
                   them onto a second line, hiding them entirely. */}
                <Form.Group widths="equal" style={{ marginTop: 6 }}>
                  <Form.Input
                    label="Arrives"
                    type="date"
                    value={d.inDate || ""}
                    onChange={(e) => set(r.id, "inDate", e.target.value)}
                  />
                  <TimeInput12h
                    label="At"
                    value={d.inTime || ""}
                    onChange={(v) => set(r.id, "inTime", v)}
                  />
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Input
                    label="Departs"
                    type="date"
                    value={d.outDate || ""}
                    onChange={(e) => set(r.id, "outDate", e.target.value)}
                  />
                  <TimeInput12h
                    label="At"
                    value={d.outTime || ""}
                    onChange={(v) => set(r.id, "outTime", v)}
                  />
                </Form.Group>
              </Form>
            );
          })}
        </Segment>
      )}
    </div>
  );
}

/* ── Attachment Preview ───────────────────────────────── */
function AttachmentPreview({ url }) {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const ext = url.split("?")[0].split(".").pop().toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
  const isPdf = ext === "pdf";
  const fileName =
    decodeURIComponent(url.split("/").pop().split("?")[0]) || "attachment";

  /* The attachment URL requires the auth header, so both preview and
     download go through an authenticated fetch → blob URL rather than a
     plain <a href> / window.open(url) which would 401. */
  const fetchBlobUrl = async () => {
    const token = localStorage.getItem("bhu_jwt_access");
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Failed to fetch file");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const handlePreview = async (e) => {
    e.preventDefault();
    if (previewing) return;
    setPreviewing(true);
    try {
      const blobUrl = await fetchBlobUrl();
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      /* revoke once the new tab has had a chance to load it */
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    if (downloading) return;
    setDownloading(true);
    try {
      const blobUrl = await fetchBlobUrl();
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      /* fallback: open in new tab */
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  /* Images are listed like any other file — no inline thumbnail. A scanned
     letter is unreadable at thumbnail size anyway, and it ate a screen's
     worth of the modal; Preview opens it full size in a new tab. */
  const fileIcon = isPdf
    ? "file pdf outline"
    : isImage
      ? "file image outline"
      : "file alternate outline";

  return (
    <Segment>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Icon name={fileIcon} size="large" />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <strong>{fileName}</strong>
          <span style={{ opacity: 0.6, fontSize: "0.82rem" }}>{ext.toUpperCase()}</span>
        </div>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <Button basic size="small" onClick={handlePreview} disabled={previewing} type="button">
            <Icon name={previewing ? "spinner" : "eye"} loading={previewing} />
            Preview
          </Button>
          <Button basic size="small" onClick={handleDownload} disabled={downloading} type="button">
            <Icon name={downloading ? "spinner" : "download"} loading={downloading} />
            {downloading ? "Downloading…" : "Download"}
          </Button>
        </div>
      </div>
    </Segment>
  );
}

/* Every room across every guest house the admin is authorized for, each
   tagged with its own guest house so selections can span houses, and with
   the two independent facts the pickers need.

   They are kept apart on purpose. `avail` is the server's verdict about the
   *stay window*: `available-rooms` was asked about this booking's dates, and
   a room it didn't return is held by someone else then. `underMaintenance`
   is about the room *right now* — a cleaning hold put on at this morning's
   checkout says nothing about a stay three weeks out.

   Folding the second into the first is what hid rooms: a room on a
   post-checkout hold dropped out of the dropdown for every future booking,
   and silently, since the options list is built from the vacant ones alone.
   Guest houses default to `auto_release_maintenance` off, where that hold
   has no expiry at all and waits on housekeeping, so the room stayed hidden
   for days.

   The backend already draws this line and says so out loud: guesthouses/
   views.py `available_rooms` deliberately returns rooms under maintenance
   ("Maintenance is a statement about the room right now, not about the
   window being booked"), and neither the confirm nor the add-room guard
   looks at Room.status — check-in is the gate that refuses a room still
   held on the day. So the picker offers them, flagged, and lets the admin
   decide; hiding them second-guesses a server that would have accepted the
   assignment. */
export function deriveRoomAvailability(rooms, guestHouses, availableRoomIds) {
  const ghNameById = new Map(guestHouses.map((g) => [Number(g.id), g.name]));
  return rooms
    .filter((r) => ghNameById.has(Number(r.guestHouseId)))
    .map((r) => ({
      ...r,
      id: Number(r.id) /* ensure ID is always a Number */,
      guestHouseId: Number(r.guestHouseId),
      guestHouseName: ghNameById.get(Number(r.guestHouseId)),
      avail: availableRoomIds.has(Number(r.id)) ? "vacant" : "occupied",
      underMaintenance: r.status === "maintenance",
    }))
    .sort((a, b) =>
      a.guestHouseName.localeCompare(b.guestHouseName) ||
      a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }),
    );
}

/* ── Booking Detail Modal ─────────────────────────────── */
export default function BookingDetailModal({
  booking,
  rooms,
  guestHouses,
  /* Room types keyed by guest house id — { [ghId]: [{ id, name, capacity }] }.
     Needed by the preference editor, which asks for counts per room type. */
  roomTypes = EMPTY_OBJECT,
  paymentOptions = EMPTY_ARRAY,
  onClose,
  onApprove,
  onReject,
  onPartialConfirm,
  onAddRoom,
  onTransfer,
  onCancel,
  onReschedule,
  onEditDetails,
  canFieldEdit = false,
  onFieldEdit,
  onRoomFieldEdit,
  onCheckin,
  onCheckout,
  onExtend,
  onChangeRoom,
  onRemoveRoom,
  onSetRoomGuestName,
  onReuseDetails,
  onSendMail,
  /* (bookingId) => go to the bill page; owned by the dashboard's router. */
  onOpenBill,
}) {
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  /* Which guest house's rooms are currently being browsed in the Room
     Allocation panel — picks persist in selectedRoomIds when switching
     tabs, so an admin can build up a selection spanning several houses. */
  const [viewGhId, setViewGhId] = useState(null);
  /* Separate state for the Add Room panel (adding extra rooms to an
     already-confirmed booking) — kept independent of the Room Allocation
     panel above since both can be visible at once for PARTIALLY_CONFIRMED. */
  const [addRoomIds, setAddRoomIds] = useState([]);
  const [addRoomViewGhId, setAddRoomViewGhId] = useState(null);
  const [addRoomSubmitting, setAddRoomSubmitting] = useState(false);
  const [actionMode, setActionMode] =
    useState("confirm"); /* 'confirm' | 'partial' */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferGhId, setTransferGhId] = useState("");
  const [transferRemarks, setTransferRemarks] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRemarks, setCancelRemarks] = useState("");
  /* Shared across the four action dialogs — only one is ever open at a time. */
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [transferHistoryOpen, setTransferHistoryOpen] = useState(false);
  const [activityHistoryOpen, setActivityHistoryOpen] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [mailHistoryOpen, setMailHistoryOpen] = useState(false);
  const [mailLog, setMailLog] = useState([]);
  const [mailLogLoading, setMailLogLoading] = useState(false);
  const [expandedMailId, setExpandedMailId] = useState(null);
  const [sendMailOpen, setSendMailOpen] = useState(false);
  /* Append-only internal remarks. `remarks`/`setRemarks` is already taken by
     the confirm dialog's one-shot remark, hence the distinct names. */
  const [remarkList, setRemarkList] = useState(booking.remarks || EMPTY_ARRAY);
  const [remarkDraft, setRemarkDraft] = useState("");
  const [remarkSubmitting, setRemarkSubmitting] = useState(false);
  const [remarkError, setRemarkError] = useState("");
  /* Collapsed by default — the rail's job is Room Allocation and the action
     buttons, so remarks stay out of the way until asked for. */
  const [remarksOpen, setRemarksOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    arrivalDate: "", arrivalTime: "", departureDate: "", departureTime: "", remarks: "",
  });
  const [rescheduleError, setRescheduleError] = useState("");
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editAttachmentFile, setEditAttachmentFile] = useState(null);
  const [editDetailsError, setEditDetailsError] = useState("");
  /* Four preference slots, position = priority. Room requests are carried as
     objects rather than a keyed map so a guest house this admin can't see
     (and whose room types therefore never loaded) still round-trips intact. */
  const [editPrefs, setEditPrefs] = useState([]);
  const [prefPanelOpen, setPrefPanelOpen] = useState({});
  /* Room types per guest house, seeded from what the dashboard already loaded
     and topped up on demand — same lazy fetch the booking form does, so a
     house outside this admin's own scope still lists its types. */
  const [editRoomTypesByGh, setEditRoomTypesByGh] = useState({});
  const fetchingRoomTypeGhIds = useRef(new Set());
  /* Room-level actions (Check In / No Show / Check Out / Change Stay Window / Change
     Room) on the "Room / Stay Details" rows below. `roomActionOpen` holds
     the dialog for the two actions that need a form; the confirm-only
     actions (check-in, no-show, check-out) go straight through a Popup. */
  const [roomActionOpen, setRoomActionOpen] = useState(null); /* { type: 'extend' | 'changeRoom' | 'removeRoom', assignment } */
  const [roomActionSubmitting, setRoomActionSubmitting] = useState(null); /* `${assignmentId}:${action}` */
  const [roomActionError, setRoomActionError] = useState("");
  const [checkinWarning, setCheckinWarning] = useState("");
  const [extendDate, setExtendDate] = useState("");
  const [extendTime, setExtendTime] = useState("");
  /* Blank unless the admin moves the arrival too — the dialog edits both ends
     of one room's stay now, and an untouched end must stay untouched. */
  const [extendArrivalDate, setExtendArrivalDate] = useState("");
  const [extendArrivalTime, setExtendArrivalTime] = useState("");
  /* Per-room stay windows staged in the allocation / add-room pickers.
     { [roomId]: { inDate, inTime, outDate, outTime } } — empty means every
     room simply inherits the booking's window. */
  const [confirmRoomWindows, setConfirmRoomWindows] = useState({});
  const [addRoomWindows, setAddRoomWindows] = useState({});

  /* Opening an indent marks it read for every admin (a no-op once read). */
  const { markBookingSeen } = useBooking();
  useEffect(() => {
    markBookingSeen(booking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);
  const [changeRoomNewId, setChangeRoomNewId] = useState("");
  const [removeRoomRemarks, setRemoveRoomRemarks] = useState("");
  const [changeRoomViewGhId, setChangeRoomViewGhId] = useState(null);
  /* Scoped to the room being vacated, not the whole booking: that room may
     depart earlier than the booking does, and change-room is only checked as
     far as its own departure (see hooks/useVacantRooms). This instance feeds
     the Change Room dialog alone — Add Room has its own instance, over `addRoomWin`. */
  const { vacancyLoading: changeRoomVacancyLoading, newRoomOptionsByGh } =
    useVacantRooms(guestHouses, booking, roomActionOpen?.type === "changeRoom", {
      forAssignment: roomActionOpen?.type === "changeRoom" ? roomActionOpen.assignment : null,
    });
  /* Authoritative, date-scoped room availability from the backend — replaces
     computing vacancy client-side from the in-memory rooms/bookings lists
     (which are only as fresh as the last full reload). */
  const [availableRoomIds, setAvailableRoomIds] = useState(new Set());
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const openConfirm = (mode) => {
    setActionMode(mode);
    setRemarks("");
    setConfirmOpen(true);
  };

  /* ── booking fields (API data wins; JSON seed is fallback) ── */
  const jd = bookingRequestFormData[toJsonKey(booking.bookingCode)] || {};

  const apName =
    booking.applicantName ||
    booking.userName ||
    jd.applicantNameAndEmployeeId?.split("|")[0]?.trim() ||
    "";
  const apEmpId =
    booking.employeeId ||
    jd.applicantNameAndEmployeeId?.split("|")[1]?.trim() ||
    "";
  const apDesig =
    booking.applicantDesignation ||
    booking.designation ||
    jd.applicantDesignationDepartment?.split("|")[0]?.trim() ||
    "";
  const apDept =
    booking.applicantDepartment ||
    booking.department ||
    jd.applicantDesignationDepartment?.split("|")[1]?.trim() ||
    "";
  const apMobile =
    booking.applicantMobileNumber ||
    booking.phone ||
    jd.applicantMobileNumber ||
    "";
  const apEmail = booking.emailId || booking.email || jd.emailId || "";

  const gName = booking.guestName || jd.guestName || "";
  const gDesig = booking.guestDesignation || jd.guestDesignation || "";
  const gAddr = booking.guestAddress || jd.guestAddress || "";
  const gMobile = booking.guestMobileNumber || jd.guestMobileNumber || "";
  const gRoomCount =
    booking.roomCount || String(booking.guests || "") || jd.roomCount || "";
  const gPurpose =
    booking.visitPurpose || booking.purpose || jd.visitPurpose || "";
  const gCount = booking.accompanyingPersonsCount || jd.accompanyingPersonsCount || 0;
  const gGuestCount = 1 + gCount;

  const sDays =
    booking.numberOfDays ||
    jd.stayDays ||
    stayNights(booking.checkIn, booking.checkOut);
  const { checkin: actualCheckin, checkout: actualCheckout } = getActualCheckInOut(booking);
  /* The detail fetch maps the booking without the option list, so the raw
     code is all it carries — resolve it to a label here instead of showing
     the applicant "SELF". */
  const paymentLabel =
    paymentOptions.find((o) => o.code === booking.paymentResponsibility)?.label ||
    booking.payment ||
    booking.paymentMode ||
    jd.paymentModes?.find((p) => p.checked)?.label ||
    "—";
  const prefNames = booking.guestHousePreferences?.length
    ? booking.guestHousePreferences
    : guestHouses.map((g) => g.name);

  /* ── status-based flags ── */
  const apiStatus = booking._apiStatus || "";
  const isActionable =
    apiStatus === "PENDING" || apiStatus === "PARTIALLY_CONFIRMED";
  const isConfirmed = apiStatus === "CONFIRMED";
  /* Room stays assigned (and worth showing) through Checked In/Out/No Show —
     not just while status is still literally "Confirmed". */
  const hasAssignedRoom = STATUSES_WITH_ASSIGNED_ROOM.includes(booking.status);
  /* Cancel is available for any non-terminal status */
  const canCancel = ["PENDING", "CONFIRMED", "PARTIALLY_CONFIRMED"].includes(apiStatus);
  /* Reschedule (prepone/postpone) only applies before check-in has happened.
     PENDING is included: nothing is allotted yet, so moving the dates is just
     an amendment to the request rather than a re-allocation. */
  const canReschedule = ["PENDING", "CONFIRMED", "PARTIALLY_CONFIRMED"].includes(apiStatus);
  const isPending = apiStatus === "PENDING";
  const lastTransfer = latestTransfer(booking);
  /* Preferences drive the allotment, so they stop being editable the moment
     rooms are held — mirrors the backend's PENDING-only rule. */
  const canEditPreferences = isPending;
  /* Editing applicant/guest identity details serves no purpose once a stay is closed out. */
  /* TRANSFERRED: the sender's read-only view of a booking it passed on (the
     backend refuses its edits; the guest house holding it now edits it). */
  const canEditDetails = !["CANCELLED", "REJECTED", "CHECKED_OUT", "NO_SHOW", "TRANSFERRED"].includes(apiStatus);
  /* Extra rooms (beyond the original allocation) can be added once a booking
     already holds at least one room — mirrors the backend's allowed
     statuses for the add-room action. */
  const canAddRoom = ["CONFIRMED", "PARTIALLY_CONFIRMED", "CHECKED_IN"].includes(apiStatus);

  /* Rooms the applicant asked for — the per-preference room-type counts are
     expected to add up to this, same rule the booking form applies. */
  const roomsRequired = Number(booking.roomCount) || 0;
  /* The value being typed in the Edit Details form. The preference counters
     below cap against this rather than the saved count, so raising the rooms
     count and entering the extra room happen in one pass. */
  const editRoomsRequired = Number(editForm.roomsRequired) || 0;

  /* ── edit-form option lists ── */
  /* Payment responsibility options are scoped to the booking's own type; an
     option that has since been deactivated is still offered if it's the one
     currently on the booking, so opening the form can't silently drop it. */
  const paymentDropdownOptions = useMemo(() => {
    const forType = paymentOptions.filter(
      (o) => o.booking_type === booking.bookingTypeCode && (o.is_active || o.code === booking.paymentResponsibility),
    );
    return forType.map((o) => ({ key: o.code, value: o.code, text: o.label }));
  }, [paymentOptions, booking.bookingTypeCode, booking.paymentResponsibility]);

  /* ── inline per-field editor option lists (has_edit_access admins) ── */
  const BOOKING_TYPE_OPTIONS = [
    { key: "OFFICIAL", value: "OFFICIAL", text: "Official" },
    { key: "NON_OFFICIAL", value: "NON_OFFICIAL", text: "Non-Official" },
  ];
  const FIELD_STATUS_OPTIONS = [
    { key: "PENDING", value: "PENDING", text: "Pending" },
    { key: "CONFIRMED", value: "CONFIRMED", text: "Confirmed" },
    { key: "PARTIALLY_CONFIRMED", value: "PARTIALLY_CONFIRMED", text: "Partially Confirmed" },
    { key: "REJECTED", value: "REJECTED", text: "Rejected" },
    { key: "CANCELLED", value: "CANCELLED", text: "Cancelled" },
    { key: "TRANSFERRED", value: "TRANSFERRED", text: "Transferred" },
    { key: "CHECKED_IN", value: "CHECKED_IN", text: "Checked In" },
    { key: "CHECKED_OUT", value: "CHECKED_OUT", text: "Checked Out" },
    { key: "NO_SHOW", value: "NO_SHOW", text: "No Show" },
  ];
  const assignedGhOptions = useMemo(
    () =>
      [...guestHouses]
        .map((g) => ({ key: g.id, value: g.id, text: g.name }))
        .sort((a, b) => ghCollator.compare(a.text, b.text)),
    [guestHouses],
  );

  /* An applicant can name a guest house this admin isn't scoped to see, so the
     booking's own preferences are unioned in — otherwise reopening the form
     would blank out a preference the admin never touched. */
  const prefGhOptions = useMemo(() => {
    const byId = new Map();
    (booking.guestHousePreferenceOptions || []).forEach((p) => {
      if (p.id) byId.set(Number(p.id), p.name || `Guest House #${p.id}`);
    });
    guestHouses.forEach((g) => byId.set(Number(g.id), g.name));
    return [...byId.entries()]
      .map(([id, name]) => ({ key: id, value: id, text: name }))
      .sort((a, b) => ghCollator.compare(a.text, b.text));
  }, [guestHouses, booking.guestHousePreferenceOptions]);

  /* ── room availability ──
     Fetch authoritative, date-scoped availability from the backend for every
     guest house the admin is authorized for (not just one at a time) —
     rooms can now be picked across houses in a single confirm/partial-confirm
     action. A room not returned here is held by another stay over these
     dates; a room under maintenance still comes back, and is flagged rather
     than hidden (see deriveRoomAvailability). */
  const { start: stayStart, end: stayEnd } = bookingStayWindow(booking);
  useEffect(() => {
    const hasValidRange = Boolean(stayStart && stayEnd && stayStart < stayEnd);
    if (!guestHouses.length || !hasValidRange) {
      setAvailableRoomIds(new Set());
      return;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    Promise.all(
      guestHouses.map((gh) =>
        ghApi
          .availableRooms(gh.id, stayStart, stayEnd)
          .catch(() => ({ rooms: [] })), /* one GH failing shouldn't blank out the rest */
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const ids = new Set();
        results.forEach((data) => {
          (data.rooms || []).forEach((r) => ids.add(Number(r.id)));
        });
        setAvailableRoomIds(ids);
      })
      .catch(() => {
        /* Can't confirm availability — treat everything as occupied rather
           than risk a double-booking by showing rooms as vacant. */
        if (!cancelled) setAvailableRoomIds(new Set());
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [guestHouses, stayStart, stayEnd]);

  /* `guestHouses` is already scoped server-side to the houses this admin may
     see; deriveRoomAvailability drops anything outside it. */
  const allRooms = useMemo(
    () => deriveRoomAvailability(rooms, guestHouses, availableRoomIds),
    [rooms, guestHouses, availableRoomIds],
  );

  /* Both pickers label a room the same way. The maintenance note is a
     warning, not a refusal: the room is still selectable, because the server
     accepts it and check-in is where a room still being cleaned on the day is
     actually refused. */
  const roomOptionText = (r) =>
    `${r.guestHouseName} — Room ${r.roomNumber} (${r.roomType})` +
    (r.underMaintenance ? " — under maintenance" : "");

  const selectedRooms = allRooms.filter((r) => selectedRoomIds.includes(r.id));
  /* Cap room selection at the number of rooms the applicant actually
     requested — e.g. 2 rooms requested means at most 2 can be allotted. */
  const requiredRoomCount = Number(gRoomCount) || 1;
  /* Require ALL selected IDs to resolve to a known, vacant room. */
  const canApprove =
    selectedRooms.length > 0 &&
    selectedRooms.length === selectedRoomIds.length &&
    selectedRooms.every((r) => r.avail === "vacant");

  /* Default the Guest House dropdown to the booking's current/primary guest
     house when it's one of the authorized houses, otherwise the first one. */
  useEffect(() => {
    if (viewGhId !== null || !guestHouses.length) return;
    const primary = booking.guestHouseId ? Number(booking.guestHouseId) : null;
    setViewGhId(
      primary && guestHouses.some((g) => g.id === primary) ? primary : guestHouses[0].id,
    );
  }, [guestHouses, booking.guestHouseId, viewGhId]);

  const vacantRoomsByGh = useMemo(() => {
    const byGh = new Map();
    allRooms
      .filter((r) => r.avail === "vacant")
      .forEach((r) => {
        if (!byGh.has(r.guestHouseId)) byGh.set(r.guestHouseId, []);
        byGh.get(r.guestHouseId).push(r);
      });
    return byGh;
  }, [allRooms]);

  const viewGhRooms = vacantRoomsByGh.get(viewGhId) || [];
  /* Rooms already picked from a *different* guest house than the one the
     dropdown is currently showing still need to appear as options — Semantic
     UI's Dropdown only renders a chip for a selected value if that value is
     present in `options` — otherwise switching guest houses would silently
     drop earlier picks from view (and from `value`). */
  const roomDropdownOptions = [
    ...viewGhRooms,
    ...selectedRooms.filter((r) => r.guestHouseId !== viewGhId),
  ];

  /* Default the Add Room panel's Guest House dropdown the same way. */
  useEffect(() => {
    if (addRoomViewGhId !== null || !guestHouses.length) return;
    const primary = booking.guestHouseId ? Number(booking.guestHouseId) : null;
    setAddRoomViewGhId(
      primary && guestHouses.some((g) => g.id === primary) ? primary : guestHouses[0].id,
    );
  }, [guestHouses, booking.guestHouseId, addRoomViewGhId]);

  /* Add Room asks about its own window, not the booking's whole stay: on a
     booking that has already begun, a room only has to be free from now on.
     Asking about the whole stay hid any room that freed up part-way through —
     including one this booking checked out of and wants back. The same window
     is sent with every added room (addRoomPayloadWindows), so the POST checks
     exactly what this list offered. */
  const [addRoomWin, setAddRoomWin] = useState(() => addRoomWindow(booking));
  useEffect(() => {
    setAddRoomWin(addRoomWindow(booking));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id, booking.checkIn, booking.arrivalTime, booking.checkOut, booking.departureTime]);
  const { vacancyLoading: addRoomVacancyLoading, newRoomOptions: addRoomVacant } =
    useVacantRooms(guestHouses, booking, canAddRoom && Boolean(onAddRoom), { window: addRoomWin });
  /* Labels (incl. the maintenance note) come from `allRooms`; only the
     vacancy itself comes from the Add Room window. */
  const addRoomCandidates = useMemo(() => {
    const ids = new Set(addRoomVacant.map((r) => Number(r.id)));
    return allRooms.filter((r) => ids.has(Number(r.id)));
  }, [addRoomVacant, allRooms]);
  const { kept: addRoomAvailable, hidden: addRoomHidden } = useMemo(
    () => splitReusableRooms(addRoomCandidates, booking, guestHouses),
    [addRoomCandidates, booking, guestHouses],
  );

  const addRoomSelectedRooms = allRooms.filter((r) => addRoomIds.includes(r.id));
  const addRoomViewGhRooms = addRoomAvailable.filter((r) => r.guestHouseId === addRoomViewGhId);
  const addRoomDropdownOptions = [
    ...addRoomViewGhRooms,
    ...addRoomSelectedRooms.filter((r) => r.guestHouseId !== addRoomViewGhId),
  ];

  /* Transfer targets: a GH_ADMIN's `guestHouses` list is server-scoped to
     only their own property, so for a single-guest-house admin it never has
     anywhere to transfer *to* — merge in the applicant's own stated
     preferences (which carry guest houses outside this admin's normal
     visibility) so a destination is actually offered. */
  const transferOptions = useMemo(() => {
    const byId = new Map();
    guestHouses.forEach((g) => byId.set(g.id, g.name));
    (booking.guestHousePreferenceOptions || []).forEach((p) => {
      if (!byId.has(p.id)) byId.set(p.id, p.name);
    });
    return Array.from(byId, ([id, name]) => ({ id, name }))
      .filter((g) => g.id !== booking.guestHouseId)
      /* Context hands guestHouses over already sorted, but the applicant's own
         preferences are merged in after them — re-sort so the union reads
         alphabetically like every other guest house dropdown. */
      .sort((a, b) => ghCollator.compare(a.name, b.name));
  }, [guestHouses, booking.guestHousePreferenceOptions, booking.guestHouseId]);

  /* onApprove/onReject/onPartialConfirm/onTransfer/onCancel (AdminDashboard.js)
     already catch their own errors and show a toast, so they never reject —
     these wrappers exist purely to keep the dialog open (with a spinner) for
     the duration of the request instead of closing instantly. */
  const handleConfirmAction = async () => {
    setActionSubmitting(true);
    try {
      const knownRoomIds = new Set(allRooms.map((r) => r.id));
      const roomIds = selectedRoomIds.filter((id) => knownRoomIds.has(id));
      const roomWindows = buildRoomWindows(
        Object.fromEntries(roomIds.map((id) => [id, confirmRoomWindows[id]])),
      );
      if (actionMode === "partial") {
        await onPartialConfirm(booking.id, roomIds, remarks);
      } else {
        await onApprove(booking.id, roomIds, remarks, roomWindows);
      }
    } finally {
      setActionSubmitting(false);
      setConfirmOpen(false);
      onClose();
    }
  };

  const handleAddRoomAction = async () => {
    if (!addRoomIds.length) return;
    setAddRoomSubmitting(true);
    try {
      const roomWindows = addRoomPayloadWindows(addRoomIds, addRoomWin, booking, addRoomWindows);
      await onAddRoom(booking.id, addRoomIds, roomWindows);
      setAddRoomIds([]);
      setAddRoomWindows({});
      setAddRoomWin(addRoomWindow(booking));
    } finally {
      setAddRoomSubmitting(false);
    }
  };

  const handleRejectAction = async () => {
    setActionSubmitting(true);
    try {
      await onReject(booking.id, rejectRemarks);
    } finally {
      setActionSubmitting(false);
      setRejectOpen(false);
      onClose();
    }
  };

  const handleTransferAction = async () => {
    setActionSubmitting(true);
    try {
      await onTransfer(booking.id, transferGhId, transferRemarks);
    } finally {
      setActionSubmitting(false);
      setTransferOpen(false);
      onClose();
    }
  };

  const handleCancelAction = async () => {
    setActionSubmitting(true);
    try {
      await onCancel?.(booking.id, cancelRemarks);
    } finally {
      setActionSubmitting(false);
      setCancelOpen(false);
      onClose();
    }
  };

  /* Reschedule and Edit Details deliberately stay open on success (unlike
     the terminal-ish actions above) — the point is to see the change land
     in place and, if needed, immediately check it in Activity History. */
  const handleRescheduleAction = async () => {
    setRescheduleError("");
    setActionSubmitting(true);
    try {
      await onReschedule(booking.id, {
        new_arrival_date: rescheduleForm.arrivalDate,
        new_arrival_time: rescheduleForm.arrivalTime,
        new_departure_date: rescheduleForm.departureDate,
        new_departure_time: rescheduleForm.departureTime,
        remarks: rescheduleForm.remarks,
      });
      setRescheduleOpen(false);
      if (activityHistoryOpen) loadAuditLog();
    } catch (e) {
      setRescheduleError(e.message || "Failed to reschedule booking.");
    } finally {
      setActionSubmitting(false);
    }
  };

  /* Four fixed slots so an admin can add a preference the applicant left out;
     empty ones are dropped on save. */
  /* Seed from the dashboard's map, then fill gaps for whichever houses the
     admin actually selects. Guarded per-id so re-renders don't re-request. */
  useEffect(() => {
    setEditRoomTypesByGh((prev) => ({ ...roomTypes, ...prev }));
  }, [roomTypes]);

  useEffect(() => {
    if (!editDetailsOpen) return;
    editPrefs.forEach((pref) => {
      const ghId = pref.guestHouseId;
      if (!ghId || editRoomTypesByGh[ghId] !== undefined) return;
      if (fetchingRoomTypeGhIds.current.has(ghId)) return;
      fetchingRoomTypeGhIds.current.add(ghId);
      roomTypeApi
        .list(ghId)
        .then((types) => setEditRoomTypesByGh((prev) => ({ ...prev, [ghId]: types })))
        .catch(() => setEditRoomTypesByGh((prev) => ({ ...prev, [ghId]: [] })))
        .finally(() => fetchingRoomTypeGhIds.current.delete(ghId));
    });
  }, [editDetailsOpen, editPrefs, editRoomTypesByGh]);

  const seedEditPrefs = () => {
    const seeded = (booking.guestHousePreferenceOptions || []).slice(0, 4).map((p) => ({
      guestHouseId: Number(p.id),
      roomRequests: (p.roomRequests || []).map((rr) => ({
        roomTypeId: rr.roomTypeId,
        roomTypeName: rr.roomTypeName,
        count: rr.count,
      })),
    }));
    while (seeded.length < 4) seeded.push({ guestHouseId: "", roomRequests: [] });
    return seeded;
  };

  const setPrefGuestHouse = (idx, ghId) => {
    setEditPrefs((prev) =>
      prev.map((p, i) =>
        /* Room requests point at room types owned by the old house, so
           switching houses has to clear them. */
        i === idx ? { guestHouseId: ghId === undefined ? "" : ghId, roomRequests: [] } : p,
      ),
    );
  };

  const stepPrefRoomCount = (idx, roomType, delta) => {
    setEditPrefs((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        const existing = (p.roomRequests || []).find(
          (rr) => Number(rr.roomTypeId) === Number(roomType.id),
        );
        const rest = (p.roomRequests || []).filter(
          (rr) => Number(rr.roomTypeId) !== Number(roomType.id),
        );
        const next = Math.max(0, (Number(existing?.count) || 0) + delta);
        if (!next) return { ...p, roomRequests: rest };
        return {
          ...p,
          roomRequests: [
            ...rest,
            { roomTypeId: roomType.id, roomTypeName: roomType.name, count: next },
          ],
        };
      }),
    );
  };

  const handleEditDetailsAction = async () => {
    setEditDetailsError("");

    const payload = {
      employee_id: editForm.employeeId,
      applicant_name: editForm.applicantName,
      designation: editForm.designation,
      department: editForm.department,
      email: editForm.email,
      mobile: editForm.mobile,
      guest_name: editForm.guestName,
      guest_designation: editForm.guestDesignation,
      guest_mobile: editForm.guestMobile,
      guest_address: editForm.guestAddress,
      accompanying_persons_count: Number(editForm.accompanyingPersonsCount) || 0,
    };

    if (editForm.paymentResponsibility) {
      payload.payment_responsibility = editForm.paymentResponsibility;
      if (editForm.paymentResponsibility === OTHER_PAYMENT_CODE) {
        if (!(editForm.paymentResponsibilityOther || "").trim()) {
          setEditDetailsError("Please specify who is responsible for payment.");
          return;
        }
        payload.payment_responsibility_other = editForm.paymentResponsibilityOther.trim();
      }
    }

    /* Preferences are only sent while the booking is pending — the backend
       rejects them outright at any later status, so sending them regardless
       would fail an otherwise-valid name or payment edit. */
    if (canEditPreferences) {
      const filled = editPrefs.filter((p) => p.guestHouseId);
      if (!filled.length) {
        setEditDetailsError("At least one guest house preference is required.");
        return;
      }
      const ghIds = filled.map((p) => Number(p.guestHouseId));
      if (ghIds.length !== new Set(ghIds).size) {
        setEditDetailsError("The same guest house cannot be used for two preferences.");
        return;
      }
      /* Sent alongside the preferences and under the same guard: the backend
         only accepts a rooms count while the booking is pending, so including
         it later would fail an otherwise-valid name or payment edit. */
      const nextRooms = Number(editForm.roomsRequired) || 0;
      if (nextRooms < 1) {
        setEditDetailsError("At least one room is required.");
        return;
      }
      payload.rooms_required = nextRooms;
      /* Priority follows slot order after empty slots are dropped, so
         clearing preference 2 promotes 3 and 4 rather than leaving a gap. */
      payload.preferences = filled.map((p, i) => ({
        guest_house: Number(p.guestHouseId),
        priority: i + 1,
        room_requests: (p.roomRequests || [])
          .filter((rr) => Number(rr.count) > 0)
          .map((rr) => ({ room_type: Number(rr.roomTypeId), count: Number(rr.count) })),
      }));
    }

    setActionSubmitting(true);
    try {
      let body = payload;
      if (editAttachmentFile) {
        body = appendBookingPayload(new FormData(), payload);
        body.append("official_attachment", editAttachmentFile);
      }
      await onEditDetails(booking.id, body);
      setEditDetailsOpen(false);
      setEditAttachmentFile(null);
      if (activityHistoryOpen) loadAuditLog();
    } catch (e) {
      setEditDetailsError(e.message || "Failed to update booking details.");
    } finally {
      setActionSubmitting(false);
    }
  };

  /* Edit gate for the three detail sections only — the caller's
     has_edit_access AND the parked-feature flag above. `canFieldEdit` itself
     is left untouched so the rest of the page keeps its editors. */
  const canDetailFieldEdit = INLINE_DETAIL_EDIT_ENABLED && canFieldEdit;

  /* Per-field inline editor save — one field at a time. A throw here is
     caught by FieldEditPopup and shown in its popup, which then stays open. */
  const saveField = async (payload) => {
    await onFieldEdit(booking.id, payload);
    if (activityHistoryOpen) loadAuditLog();
  };

  /* Same, for one room assignment's timestamps. `payload` is
     { assignment_id, actual_checkin? | actual_checkout? | expected_checkin? |
     expected_checkout? }. Keyed by assignment, not room: a booking can hold the
     same room twice, so a room id no longer names one stay. */
  const saveRoomField = async (payload) => {
    await onRoomFieldEdit(booking.id, payload);
    if (activityHistoryOpen) loadAuditLog();
  };

  /* Row-level Check In / No Show / Check Out — confirm-only, no form, so
     errors surface as a toast from AdminDashboard (same as every other
     action here) rather than inline. */
  const handleRoomCheckin = async (a, noShow) => {
    /* Marking a no-show is exempt — it releases the room instead of
       occupying it, so it stays available before the arrival date. */
    const early = noShow ? [] : checkinTooEarly(booking, [a]);
    if (early.length) {
      setCheckinWarning(checkinTooEarlyMsg(booking, early));
      return;
    }
    setCheckinWarning("");
    const key = `${a.id}:${noShow ? "noshow" : "checkin"}`;
    setRoomActionSubmitting(key);
    try {
      await onCheckin(booking.id, [a.id], noShow);
      if (activityHistoryOpen) loadAuditLog();
    } finally {
      setRoomActionSubmitting(null);
    }
  };

  const handleRoomCheckout = async (a) => {
    const key = `${a.id}:checkout`;
    setRoomActionSubmitting(key);
    try {
      await onCheckout(booking.id, [a.id]);
      if (activityHistoryOpen) loadAuditLog();
    } finally {
      setRoomActionSubmitting(null);
    }
  };

  const handleSetGuestName = async (a, guestName) => {
    await onSetRoomGuestName(booking.id, a.id, guestName);
    if (activityHistoryOpen) loadAuditLog();
  };

  const openExtendDialog = (a) => {
    /* Starts from the room's current window, so either end can be moved either way. */
    setExtendDate(a.effectiveDeparture || booking.checkOut || "");
    setExtendTime((a.effectiveDepartureTime || booking.departureTime || "").slice(0, 5));
    setExtendArrivalDate(a.effectiveArrival || booking.checkIn || "");
    setExtendArrivalTime((a.effectiveArrivalTime || booking.arrivalTime || "").slice(0, 5));
    setRoomActionError("");
    setRoomActionOpen({ type: "extend", assignment: a });
  };

  /* Only a later departure can collide with another stay, so only that is
     pre-checked — mirrors FrontDeskModal's handleExtend: the window runs from
     the room's own current departure to the new one, in the room's own guest
     house since a booking's rooms can span more than one. */
  const handleRoomExtend = async () => {
    const a = roomActionOpen?.assignment;
    if (!a || !extendDate || !extendTime) {
      setRoomActionError("Select the new departure date and time.");
      return;
    }
    setRoomActionError("");
    const key = `${a.id}:extend`;
    setRoomActionSubmitting(key);
    try {
      const { start, end } = extensionWindow(
        booking, a.effectiveDeparture || booking.checkOut, extendDate,
        a.effectiveDepartureTime || booking.departureTime, extendTime,
      );
      if (end > start) {
        /* Exempt only this stay: the booking's other rooms — including a
           second stay on this same room — are genuine conflicts. */
        const data = await ghApi.availableRooms(a.guestHouseId, start, end, {
          excludeAssignment: a.id,
        });
        const availableIds = new Set((data.rooms || []).map((r) => Number(r.id)));
        if (!availableIds.has(Number(a.roomId))) {
          setRoomActionError(`Room ${a.roomNumber} is not available until ${fmtDateAndTime(extendDate, extendTime)}.`);
          return;
        }
      }
      await onExtend(booking.id, extendDate, [a.id], extendTime, {
        newArrivalDate: extendArrivalDate || "",
        newArrivalTime: extendArrivalTime || "",
      });
      setRoomActionOpen(null);
      if (activityHistoryOpen) loadAuditLog();
    } catch (e) {
      setRoomActionError(e.message || "Failed to change the departure.");
    } finally {
      setRoomActionSubmitting(null);
    }
  };

  const openChangeRoomDialog = (a) => {
    setChangeRoomNewId("");
    setChangeRoomViewGhId(a.guestHouseId || null);
    setRoomActionError("");
    setRoomActionOpen({ type: "changeRoom", assignment: a });
  };

  const handleRoomChangeRoom = async () => {
    const a = roomActionOpen?.assignment;
    if (!a || !changeRoomNewId) {
      setRoomActionError("Select the new room.");
      return;
    }
    setRoomActionError("");
    const key = `${a.id}:changeRoom`;
    setRoomActionSubmitting(key);
    try {
      await onChangeRoom(booking.id, a.id, changeRoomNewId, a.roomId);
      setRoomActionOpen(null);
      if (activityHistoryOpen) loadAuditLog();
    } catch (e) {
      setRoomActionError(e.message || "Failed to change room.");
    } finally {
      setRoomActionSubmitting(null);
    }
  };

  const openRemoveRoomDialog = (a) => {
    setRemoveRoomRemarks("");
    setRoomActionError("");
    setRoomActionOpen({ type: "removeRoom", assignment: a });
  };

  /* Removing the booking's only room cancels the booking outright — the
     backend does that in one transaction, so the dialog warns about it up
     front rather than letting the status change come as a surprise. */
  const handleRoomRemove = async () => {
    const a = roomActionOpen?.assignment;
    if (!a) return;
    if (!removeRoomRemarks.trim()) {
      setRoomActionError("A reason is required.");
      return;
    }
    setRoomActionError("");
    const key = `${a.id}:remove`;
    setRoomActionSubmitting(key);
    try {
      await onRemoveRoom(booking.id, a.id, removeRoomRemarks.trim());
      setRoomActionOpen(null);
      if (activityHistoryOpen) loadAuditLog();
    } catch (e) {
      setRoomActionError(e.message || "Failed to remove room.");
    } finally {
      setRoomActionSubmitting(null);
    }
  };

  /* The list arrives nested on GET /bookings/{id}/, so re-seed whenever the
     parent hands down a freshly fetched booking. */
  useEffect(() => {
    setRemarkList(booking.remarks || EMPTY_ARRAY);
  }, [booking.id, booking.remarks]);

  /* The POST returns the whole list back, so state is replaced rather than
     appended to — no chance of drifting out of the server's ordering. */
  const handleAddRemark = async () => {
    const text = remarkDraft.trim();
    if (!text) return;
    setRemarkError("");
    setRemarkSubmitting(true);
    try {
      setRemarkList(await bookingApi.addRemark(booking.id, text));
      setRemarkDraft("");
      if (activityHistoryOpen) loadAuditLog();
    } catch (e) {
      setRemarkError(e.message || "Failed to add remark.");
    } finally {
      setRemarkSubmitting(false);
    }
  };

  const renderRemark = (r) => (
    <div key={r.id} className="bhu-remark-item">
      <div style={{ fontSize: "0.82rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {r.text}
      </div>
      <div style={{ fontSize: "0.74rem", color: "rgba(0,0,0,0.45)", marginTop: 2 }}>
        {r.created_by_name} · {fmtDateTime(r.created_at)}
      </div>
    </div>
  );

  const loadAuditLog = async () => {
    setAuditLogLoading(true);
    try {
      const logs = await bookingApi.auditLog(booking.id);
      setAuditLog(logs || []);
    } catch {
      setAuditLog([]);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const loadMailLog = async () => {
    setMailLogLoading(true);
    try {
      setMailLog(((await bookingApi.mailLog(booking.id)) || []).map(mapSentEmail));
    } catch {
      setMailLog([]);
    } finally {
      setMailLogLoading(false);
    }
  };

  const toggleMailHistory = () => {
    setMailHistoryOpen((open) => {
      const next = !open;
      if (next && mailLog.length === 0) loadMailLog();
      return next;
    });
  };

  const handleMailSent = async () => {
    setSendMailOpen(false);
    /* Deliberately no onClose(): the panel stays open so the new row is
       visible in Emails Sent straight away. */
    setMailHistoryOpen(true);
    await loadMailLog();
    if (activityHistoryOpen) loadAuditLog();
  };

  const toggleActivityHistory = () => {
    setActivityHistoryOpen((open) => {
      const next = !open;
      if (next && auditLog.length === 0) loadAuditLog();
      return next;
    });
  };

  const AUDIT_ACTION_LABELS = {
    BOOKING_RESCHEDULED: "Rescheduled",
    BOOKING_DETAILS_EDITED: "Details Edited",
    BOOKING_FIELD_EDITED: "Field Edited",
    BOOKING_CREATED: "Created",
    BOOKING_SEEN: "Opened (marked read)",
    BOOKING_CONFIRMED: "Confirmed",
    BOOKING_PARTIALLY_CONFIRMED: "Partially Confirmed",
    BOOKING_REJECTED: "Rejected",
    BOOKING_TRANSFERRED: "Transferred",
    BOOKING_CANCELLED: "Cancelled",
    BOOKING_CANCELLED_EXTERNAL: "Cancelled (External)",
    BOOKING_CANCELLED_BY_APPLICANT: "Cancelled by Applicant",
    ROOM_ADDED: "Room Added",
    ROOM_REMOVED: "Room Removed",
    CHECKED_IN: "Checked In",
    CHECKED_OUT: "Checked Out",
    MARKED_NO_SHOW: "Marked No-Show",
    STAY_EXTENDED: "Stay Extended",
    STAY_SHORTENED: "Stay Shortened",
    ROOM_CHANGED: "Room Changed",
    ROOM_GUEST_NAME_UPDATED: "Guest Name Updated",
    BOOKING_REMARK_ADDED: "Remark Added",
    BOOKING_MAIL_SENT: "Email Sent",
    BILL_CREATED: "Bill Made",
    BILL_REFRESHED: "Bill Recalculated",
    BILL_UPDATED: "Bill Details Changed",
    BILL_DAY_UPDATED: "Meals Entered",
    BILL_LINE_ADDED: "Charge Added",
    BILL_LINE_UPDATED: "Charge Changed",
    BILL_LINE_DELETED: "Charge Removed",
    BILL_RETENTION_DECIDED: "Retention Decided",
    BILL_FINALIZED: "Bill Finalized",
    BILL_CANCELLED: "Bill Cancelled",
    PAYMENT_RECORDED: "Payment Recorded",
    PAYMENT_VOIDED: "Payment Voided",
  };

  const formatAuditFieldLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const AUDIT_DATETIME_FIELDS = new Set([
    "actual_checkin", "actual_checkout", "expected_checkin", "expected_checkout",
  ]);
  const formatAuditFieldValue = (value, key) => {
    if (AUDIT_DATETIME_FIELDS.has(key) && value) {
      return fmtDateTime(value);
    }
    if (Array.isArray(value)) return value.join(", ");
    /* Nested values, e.g. a meals-grid save: {"Dinner": ["0", "3"]} reads
       "Dinner 0 → 3". */
    if (value && typeof value === "object") {
      return Object.entries(value)
        .map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(" → ") : String(v)}`)
        .join(", ");
    }
    return String(value);
  };

  return (
    <div className="bhu-compact-detail">
      {/* Tightens the default Semantic spacing (headers, dividers, grid
         column padding, segment padding) so the whole detail view fits on
         screen without scrolling instead of every section adding ~1rem. */}
      <style>{`
        .bhu-compact-detail .ui.header { margin-top: 0; margin-bottom: 6px; }
        .bhu-compact-detail .ui.divider { margin: 8px 0; }
        .bhu-compact-detail .ui.grid { margin-top: 0; margin-bottom: 0; }
        .bhu-compact-detail .ui.grid > .column,
        .bhu-compact-detail .ui.grid > .row > .column { padding-top: 5px; padding-bottom: 5px; }
        .bhu-compact-detail .bhu-detail-main.ui.segment,
        .bhu-compact-detail .bhu-rail-card.ui.segment { padding: 12px 16px; margin: 0; }
        /* Two-column layout: read-only details on the left, a sticky rail on
           the right holding Status/Key Facts/Room Allocation/Actions — so
           the primary task never requires scrolling past the whole page.
           The rail's fixed width was 300px, which left the Room Allocation
           card's two-up date/time rows (and the Stagger arrival/departure
           fields inside it) and the room labels cramped on every desktop
           width, not just narrow ones — widened at the read-only column's
           expense rather than the whole modal's, since that side has room
           to give (three-per-row fields, plenty of blank space). */
        .bhu-detail-grid { display: grid; grid-template-columns: 1fr 460px; gap: 14px; align-items: start; }
        .bhu-detail-rail { display: flex; flex-direction: column; gap: 12px; position: sticky; top: 14px; }
        .bhu-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 10px; }
        /* Remarks thread: only ever visible while the card is expanded, so it
           can afford a few entries before scrolling. */
        .bhu-remark-list { margin-top: 10px; max-height: 150px; overflow-y: auto; padding-right: 6px; }
        .bhu-remark-item { padding: 8px 0; border-top: 1px solid rgba(34,36,38,0.1); }
        .bhu-remark-item:first-child { border-top: none; padding-top: 0; }
        .bhu-remark-list::-webkit-scrollbar { width: 6px; }
        .bhu-remark-list::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }
        /* Semantic's own .ui.form input[type=date] rules already match a
           date field's border/padding/radius to every other input — the box
           itself isn't the problem. The browser's own calendar-icon glyph
           inside it is: oversized and a different weight than Semantic's
           icon set, which is obvious right next to a Dropdown-built field
           like TimeInput12h. Nothing here touches the mm/dd/yyyy segments
           themselves (not stylable cross-browser without replacing the
           input entirely, which nothing else in this app does either — see
           RoomWindowsPanel), just shrinks and lightens that one icon. */
        .bhu-compact-detail input[type="date"]::-webkit-calendar-picker-indicator {
          opacity: 0.45;
          cursor: pointer;
          transform: scale(0.85);
        }
        .bhu-compact-detail input[type="date"]::-webkit-calendar-picker-indicator:hover {
          opacity: 0.75;
        }
        @media (max-width: 1000px) {
          .bhu-detail-grid { grid-template-columns: 1fr; }
          .bhu-detail-rail { position: static; order: -1; }
        }
      `}</style>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <Button icon circular basic onClick={onClose} title="Back" type="button">
          <Icon name="arrow left" />
        </Button>
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <span style={{ fontSize: "0.75rem", opacity: 0.6 }}>Booking Request</span>
          <strong>{booking.bookingCode}</strong>
        </div>
        {/* Raise a fresh indent for the same applicant and guest — offered
            whatever state this one is in, since re-filing a rejected or
            cancelled request is exactly when it's most useful. */}
        {onReuseDetails && (
          <Button
            basic
            color="blue"
            size="small"
            type="button"
            onClick={() => onReuseDetails(booking)}
          >
            <Icon name="copy outline" />
            Reuse Details
          </Button>
        )}
        <CopyDetailsButton booking={booking} paymentLabel={paymentLabel} guestHouses={guestHouses} />
        <PrintApplicationFormButton booking={booking} guestHouses={guestHouses} paymentOptions={paymentOptions} />
        <StatusBadge status={booking.status} />
      </div>

      <div className="bhu-detail-grid">
      <Segment className="bhu-detail-main">
        {/* 1. Applicant Details */}
        <Header as="h5" icon="id card outline" content="Applicant Details" />
        <Grid columns={3} stackable>
          <EditableF label="Name" value={apName} editable={canDetailFieldEdit} onSave={saveField} fieldKey="applicant_name" />
          <EditableF label="Employee ID" value={apEmpId} editable={canDetailFieldEdit} onSave={saveField} fieldKey="employee_id" />
          <EditableF label="Designation" value={apDesig} editable={canDetailFieldEdit} onSave={saveField} fieldKey="designation" />
        </Grid>
        <Grid columns={3} stackable style={{ marginTop: 4 }}>
          <EditableF label="Department" value={apDept} editable={canDetailFieldEdit} onSave={saveField} fieldKey="department" />
          <EditableF label="Mobile No." value={apMobile} icon="phone" editable={canDetailFieldEdit} onSave={saveField} fieldKey="mobile" />
          <EditableF label="E-Mail ID" value={apEmail} icon="envelope outline" editable={canDetailFieldEdit} onSave={saveField} fieldKey="email" />
        </Grid>

        {/* 2. Guest Details */}
        <Divider />
        <Header as="h5" icon="user outline" content="Guest Details" />
        <Grid columns={3} stackable>
          <EditableF label="Name of the Guest" value={gName} editable={canDetailFieldEdit} onSave={saveField} fieldKey="guest_name" />
          <EditableF label="Designation" value={gDesig} editable={canDetailFieldEdit} onSave={saveField} fieldKey="guest_designation" />
          <EditableF label="Mobile Number" value={gMobile} icon="phone" copyable editable={canDetailFieldEdit} onSave={saveField} fieldKey="guest_mobile" />
        </Grid>
        <Grid columns={3} stackable style={{ marginTop: 4 }}>
          <EditableF
            label="Booking Type"
            value={booking.bookingType || "—"}
            editable={canDetailFieldEdit}
            onSave={saveField}
            fieldKey="booking_type"
            type="select"
            options={BOOKING_TYPE_OPTIONS}
            draftValue={booking.bookingTypeCode}
          />
          <EditableF label="Purpose of Visit" value={gPurpose} editable={canDetailFieldEdit} onSave={saveField} fieldKey="purpose_of_visit" type="textarea" />
          <EditableF label="Address" value={gAddr || "—"} editable={canDetailFieldEdit} onSave={saveField} fieldKey="guest_address" type="textarea" draftValue={gAddr} />
        </Grid>
        {(gCount > 0 || canDetailFieldEdit) && (
          <Grid columns={1} stackable style={{ marginTop: 4 }}>
            <EditableF
              label="Accompanying Persons"
              value={String(gCount)}
              editable={canDetailFieldEdit}
              onSave={saveField}
              fieldKey="accompanying_persons_count"
              type="number"
              min={0}
            />
          </Grid>
        )}

        {/* 3. Stay Information */}
        <Divider />
        <Header as="h5" icon="calendar alternate outline" content="Stay Information" />
        {/* Still branches on `canFieldEdit`, not the flag: an edit-access admin
            keeps the broken-out arrival/departure/rooms rows they already had
            — only the pencils on them are hidden. */}
        <Grid columns={hasAssignedRoom ? 4 : 2} stackable>
          {canFieldEdit ? (
            <>
              <EditableF label="Arrival Date" value={fmtDate(booking.checkIn)} editable={canDetailFieldEdit} onSave={saveField} fieldKey="arrival_date" type="date" draftValue={booking.checkIn} />
              <EditableF label="Arrival Time" value={booking.arrivalTime || "—"} editable={canDetailFieldEdit} onSave={saveField} fieldKey="arrival_time" type="time" draftValue={booking.arrivalTime} />
              <EditableF label="Departure Date" value={fmtDate(booking.checkOut)} editable={canDetailFieldEdit} onSave={saveField} fieldKey="departure_date" type="date" draftValue={booking.checkOut} />
              <EditableF label="Departure Time" value={booking.departureTime || "—"} editable={canDetailFieldEdit} onSave={saveField} fieldKey="departure_time" type="time" draftValue={booking.departureTime} />
              <EditableF label="Rooms Required" value={String(roomsRequired)} editable={canDetailFieldEdit} onSave={saveField} fieldKey="rooms_required" type="number" min={1} warning="Changes the stored count only — no rooms are assigned or released and allocation is not re-run." />
            </>
          ) : (
            <>
              <F label="Expected Arrival" value={fmtDateAndTime(booking.checkIn, booking.arrivalTime)} />
              <F label="Expected Departure" value={fmtDateAndTime(booking.checkOut, booking.departureTime)} />
            </>
          )}
          {/* Stamped by the front desk at check-in/checkout, so they only mean
              anything once the booking actually holds rooms. Read-only while
              INLINE_DETAIL_EDIT_ENABLED is off; when it is on, editable inline
              for a has_edit_access admin with a single room (it maps
              unambiguously to that room). With several rooms these stay a
              read-only roll-up and each room is edited in the rows below —
              those per-room pencils are unaffected by the flag. */}
          {hasAssignedRoom && (
            canDetailFieldEdit && onRoomFieldEdit && booking.roomAssignments?.length === 1 ? (
              <>
                <EditableF
                  label="Actual Arrival"
                  value={fmtDateTime(actualCheckin)}
                  editable={canDetailFieldEdit}
                  fieldKey="actual_checkin"
                  type="datetime"
                  draftValue={actualCheckin}
                  onSave={(p) => saveRoomField({ assignment_id: booking.roomAssignments[0].id, ...p })}
                />
                <EditableF
                  label="Actual Departure"
                  value={fmtDateTime(actualCheckout)}
                  editable={canDetailFieldEdit}
                  fieldKey="actual_checkout"
                  type="datetime"
                  draftValue={actualCheckout}
                  onSave={(p) => saveRoomField({ assignment_id: booking.roomAssignments[0].id, ...p })}
                />
              </>
            ) : (
              <>
                <F label="Actual Arrival" value={fmtDateTime(actualCheckin)} />
                <F label="Actual Departure" value={fmtDateTime(actualCheckout)} />
              </>
            )
          )}
        </Grid>
        {/* Check-in and checkout are per room, so these two are a roll-up —
            earliest arrival, and a departure only once every occupied room has
            been closed out. The per-room stamps are in the room rows below. */}
        {hasAssignedRoom && booking.roomAssignments?.length > 1 && (
          <div style={{ fontSize: "0.78rem", opacity: 0.65, marginTop: 4 }}>
            Actual times are across all rooms — earliest arrival, and departure once every room is checked out.
          </div>
        )}
        {/* 4. Guest House Preferences & Payment */}
        <Divider />
        <Header as="h5" icon="building outline" content="Guest House Preferences & Payment" />
        <Grid columns={5} stackable>
          {[0, 1, 2, 3].map((i) => {
            const roomRequests = booking.guestHousePreferenceOptions?.[i]?.roomRequests || [];
            const breakdown = roomRequests
              .filter((r) => r.count)
              .map((r) => `${r.count} x ${r.roomTypeName || "Room"}`)
              .join(", ");
            return (
              <F
                key={i}
                label={`Preference ${i + 1}`}
                value={
                  breakdown
                    ? `${prefNames[i] || "—"} (${breakdown})`
                    : prefNames[i] || "—"
                }
              />
            );
          })}
          <EditableF
            label="Payment Mode"
            value={paymentLabel}
            editable={canFieldEdit}
            onSave={saveField}
            fieldKey="payment_responsibility"
            type="select"
            options={paymentDropdownOptions}
            draftValue={booking.paymentResponsibility}
          />
          {booking.paymentResponsibility === OTHER_PAYMENT_CODE && (
            <EditableF
              label="Please Specify"
              value={booking.paymentResponsibilityOther || "—"}
              editable={canFieldEdit}
              onSave={saveField}
              fieldKey="payment_responsibility_other"
              type="text"
              draftValue={booking.paymentResponsibilityOther}
            />
          )}
        </Grid>

        {/* 5. Official Attachment */}
        {booking.bookingType === "Official" && booking.officialAttachment && (
          <>
            <Divider />
            <Header as="h5" icon="paperclip" content="Official Attachment" />
            <AttachmentPreview url={booking.officialAttachment} />
          </>
        )}

        {/* 6. Assigned Rooms — one-line summary bar, grouped by guest house
            (rooms can now be assigned from more than one). */}
        {hasAssignedRoom &&
          booking.roomAssignments?.length > 0 &&
          (() => {
            const assignments = booking.roomAssignments.map((a) => {
              /* Match on the room id only. Room numbers repeat across guest
                 houses (103 is a Suite in one and a Standard Room in another),
                 so a number-only fallback pulls the room type off the wrong
                 room. Without an id match we simply show no type. */
              const roomRecord = rooms.find(
                (r) => Number(r.id) === Number(a.roomId),
              );
              return {
                ...a,
                roomType: a.roomType || roomRecord?.roomType || "",
                guestHouseName: a.guestHouseName || roomRecord?.guestHouseName || booking.guestHouseName || "",
              };
            });
            const groups = [];
            const groupByGh = new Map();
            assignments.forEach((a) => {
              const key = a.guestHouseName || "—";
              if (!groupByGh.has(key)) {
                const group = { guestHouseName: key, rooms: [] };
                groupByGh.set(key, group);
                groups.push(group);
              }
              groupByGh.get(key).rooms.push(a);
            });
            return (
              <>
                <Divider />
                <Message positive>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: groups.length > 0 ? 6 : 0 }}>
                    <span style={{ fontWeight: 600 }}>Assigned Rooms</span>
                    <span>
                      <Icon name="check circle" />
                      Room Assignment Confirmed
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {groups.map((g) => (
                      <Label.Group key={g.guestHouseName}>
                        <Label basic color="blue">
                          <Icon name="building outline" />
                          {g.guestHouseName}
                        </Label>
                        {g.rooms.map((a, i) => (
                          <Label basic key={i}>
                            <Icon name="door open" />
                            Room <strong>{a.roomNumber}</strong>
                            {a.roomType ? ` · ${a.roomType}` : ""}
                          </Label>
                        ))}
                      </Label.Group>
                    ))}
                  </div>
                </Message>
              </>
            );
          })()}

        {/* Room / Stay Details — check-in/out timestamps, no-show, extensions */}
        {hasAssignedRoom && booking.roomAssignments?.length > 0 && (
          <>
            <Divider />
            <Header as="h5" icon="door open" content="Room / Stay Details" />
            {checkinWarning && (
              <Message
                warning
                icon="clock outline"
                content={checkinWarning}
                onDismiss={() => setCheckinWarning("")}
                style={{ marginBottom: 10 }}
              />
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {booking.roomAssignments.map((a, i) => {
                const notCheckedIn = !a.actualCheckin && !a.actualCheckout && !a.isNoShow;
                const checkedInActive = a.actualCheckin && !a.actualCheckout && !a.isNoShow;
                /* Only a finished stay gets a duration — a room still in-house
                   would otherwise show a figure that keeps changing. */
                const stay = hourBlockDuration(a.actualCheckin, a.actualCheckout);
                /* Available before arrival too, not just mid-stay: this dialog
                   now moves either end of the room's own stay window, and a
                   room allotted on a long indent most often needs staggering
                   between approval and arrival. The backend's `extend` accepts
                   CONFIRMED as well as CHECKED_IN; a room already checked out
                   or flagged no-show is refused there, so it stays hidden. */
                const canExtend =
                  !a.actualCheckout &&
                  !a.isNoShow &&
                  ["CONFIRMED", "CHECKED_IN"].includes(apiStatus);
                /* A room can be swapped before the guest arrives too, not just
                   mid-stay: the backend accepts change-room for CONFIRMED as
                   well as CHECKED_IN, and an allotted room often has to move
                   between approval and arrival. A room that is checked out or
                   flagged no-show is refused server-side, so it keeps the
                   button hidden. */
                const canChangeRoom =
                  !a.actualCheckout &&
                  !a.isNoShow &&
                  ["CONFIRMED", "CHECKED_IN"].includes(apiStatus);
                return (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    flexWrap: "wrap",
                    paddingBottom: i < booking.roomAssignments.length - 1 ? 10 : 0,
                    borderBottom:
                      i < booking.roomAssignments.length - 1
                        ? "1px solid rgba(0,0,0,0.08)"
                        : "none",
                  }}
                >
                  <Label basic>
                    <Icon name="door open" />
                    Room <strong>{a.roomNumber}</strong>
                    {a.roomType ? ` · ${a.roomType}` : ""}
                    {a.guestHouseName ? ` (${a.guestHouseName})` : ""}
                  </Label>
                  {a.isNoShow ? (
                    <Label color="red" basic>
                      <Icon name="user times" />
                      No Show
                    </Label>
                  ) : (
                    <>
                      <span style={{ fontSize: "0.82rem", display: "inline-flex", alignItems: "center" }}>
                        <Icon name="sign in" />
                        Checked in:{" "}
                        {a.actualCheckin ? fmtDateTime(a.actualCheckin) : "—"}
                        {canFieldEdit && onRoomFieldEdit && (
                          <FieldEditPopup
                            label={`Room ${a.roomNumber} — checked in`}
                            type="datetime"
                            seed={a.actualCheckin}
                            warning="Written raw — no room status change, no status-history row."
                            onSave={(v) => saveRoomField({ assignment_id: a.id, actual_checkin: v })}
                          />
                        )}
                      </span>
                      <span style={{ fontSize: "0.82rem", display: "inline-flex", alignItems: "center" }}>
                        <Icon name="sign out" />
                        Checked out:{" "}
                        {a.actualCheckout ? fmtDateTime(a.actualCheckout) : "—"}
                        {canFieldEdit && onRoomFieldEdit && (
                          <FieldEditPopup
                            label={`Room ${a.roomNumber} — checked out`}
                            type="datetime"
                            seed={a.actualCheckout}
                            warning="Written raw — no room status change, no status-history row."
                            onSave={(v) => saveRoomField({ assignment_id: a.id, actual_checkout: v })}
                          />
                        )}
                      </span>
                      <span style={{ fontSize: "0.82rem", display: "inline-flex", alignItems: "center" }}>
                        <Icon name="calendar outline" />
                        Expected:{" "}
                        {fmtDateAndTime(
                          a.effectiveArrival || booking.checkIn,
                          a.effectiveArrivalTime || booking.arrivalTime,
                        )}
                        {" — "}
                        {fmtDateAndTime(
                          a.effectiveDeparture || booking.checkOut,
                          a.effectiveDepartureTime || booking.departureTime,
                        )}
                        {/* Read-only on purpose: the expected window is edited
                            through Change Stay Window, which checks the new
                            window against every other stay before committing.
                            A raw inline edit would move what this room blocks
                            with no such check. One field, one editor. */}
                      </span>
                      {stay && (
                        <span style={{ fontSize: "0.82rem", opacity: 0.65 }}>
                          <Icon name="clock outline" />
                          {stay.hours} hrs ({stay.days} day{stay.days > 1 ? "s" : ""} @ 24-hr blocks)
                        </span>
                      )}
                    </>
                  )}
                  {onSetRoomGuestName && (
                    <GuestNameField
                      value={a.guestName}
                      onSave={(name) => handleSetGuestName(a, name)}
                    />
                  )}
                  {a.hasCustomWindow && (
                    <Label color="orange" basic title="This room is staggered off the booking's own dates">
                      <Icon name="calendar alternate outline" />
                      Own dates
                    </Label>
                  )}
                  <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
                    {onCheckin && notCheckedIn && (
                      <>
                        <ConfirmButton
                          icon="sign in"
                          text="Check In"
                          color="blue"
                          loading={roomActionSubmitting === `${a.id}:checkin`}
                          confirmText={`Check in Room ${a.roomNumber}?`}
                          onConfirm={() => handleRoomCheckin(a, false)}
                        />
                        <ConfirmButton
                          icon="user times"
                          text="No Show"
                          color="orange"
                          basic
                          loading={roomActionSubmitting === `${a.id}:noshow`}
                          confirmText={`Mark Room ${a.roomNumber} as no-show?`}
                          onConfirm={() => handleRoomCheckin(a, true)}
                        />
                      </>
                    )}
                    {onRemoveRoom && notCheckedIn && (
                      <Button size="mini" basic color="red" type="button" onClick={() => openRemoveRoomDialog(a)}>
                        <Icon name="trash alternate" />
                        Remove Room
                      </Button>
                    )}
                    {onCheckout && checkedInActive && (
                      <ConfirmButton
                        icon="sign out"
                        text="Check Out"
                        color="grey"
                        basic
                        loading={roomActionSubmitting === `${a.id}:checkout`}
                        confirmText={`Check out Room ${a.roomNumber}?`}
                        onConfirm={() => handleRoomCheckout(a)}
                      />
                    )}
                    {onExtend && canExtend && (
                      <Button size="mini" basic color="violet" type="button" onClick={() => openExtendDialog(a)}>
                        <Icon name="calendar alternate outline" />
                        Change Stay Window
                      </Button>
                    )}
                    {onChangeRoom && canChangeRoom && (
                      <Button size="mini" basic color="blue" type="button" onClick={() => openChangeRoomDialog(a)}>
                        <Icon name="exchange" />
                        Change Room
                      </Button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}

        {/* 6. Partial Confirm Note */}
        {booking.partialConfirmNote && (
          <>
            <Divider />
            <Header as="h5" icon="info circle" content="Partial Confirm Note" />
            <p>{booking.partialConfirmNote}</p>
          </>
        )}

        {/* Transfer History — from/to guest house, who moved it, and why */}
        {booking.transfers?.length > 0 && (
          <>
            <Divider />
            <div
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => setTransferHistoryOpen((o) => !o)}
            >
              <Header as="h5" icon="exchange" content="Transfer History" style={{ marginBottom: 0 }} />
              <Icon name={transferHistoryOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
            </div>
            {transferHistoryOpen && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
              {booking.transfers.map((t, i) => (
                <div
                  key={t.id || i}
                  style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
                >
                  <Icon name="exchange" />
                  <span>
                    <strong>{t.from_gh_name || "—"}</strong>{" "}
                    <Icon name="long arrow alternate right" />{" "}
                    <strong>{t.to_gh_name || "—"}</strong>
                  </span>
                  {t.transferred_by_name && (
                    <span style={{ fontSize: "0.82rem", opacity: 0.7 }}>
                      by {t.transferred_by_name}
                    </span>
                  )}
                  {t.transferred_at && (
                    <span style={{ fontSize: "0.78rem", opacity: 0.6 }}>
                      {fmtDateTime(t.transferred_at)}
                    </span>
                  )}
                  {t.remarks && (
                    <span style={{ fontSize: "0.82rem", fontStyle: "italic" }}>
                      {t.remarks}
                    </span>
                  )}
                </div>
              ))}
            </div>
            )}
          </>
        )}

        {/* 8. Status History (transfer entries are hidden — this is a status trail, not a transfer log) */}
        {booking.statusHistory?.filter((h) => !isTransferHistoryEntry(h)).length > 0 && (
          <>
            <Divider />
            <div
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => setHistoryOpen((o) => !o)}
            >
              <Header as="h5" icon="history" content="Status History" style={{ marginBottom: 0 }} />
              <Icon name={historyOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
            </div>
            {historyOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                {booking.statusHistory
                  .filter((h) => !isTransferHistoryEntry(h))
                  .map((h, i) => {
                    const feStatus = API_TO_FE_STATUS[h.new_status] || h.new_status;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <StatusBadge status={feStatus} />
                        {h.changed_at && (
                          <span style={{ fontSize: "0.78rem", opacity: 0.6 }}>
                            {fmtDateTime(h.changed_at)}
                          </span>
                        )}
                        {h.remarks && (
                          <span style={{ fontSize: "0.82rem", fontStyle: "italic" }}>{h.remarks}</span>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </>
        )}

        {/* 9. Activity History — reschedule + applicant/guest-detail edits, from the
           generic audit log (shared section so we don't split these across tasks). */}
        <Divider />
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={toggleActivityHistory}
        >
          <Header as="h5" icon="clipboard list" content="Activity History" style={{ marginBottom: 0 }} />
          <Icon name={activityHistoryOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
        </div>
        {activityHistoryOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            {auditLogLoading ? (
              <span style={{ fontSize: "0.82rem", opacity: 0.6 }}>Loading…</span>
            ) : auditLog.length === 0 ? (
              <span style={{ fontSize: "0.82rem", opacity: 0.6 }}>No activity recorded yet.</span>
            ) : (
              auditLog.map((entry) => {
                /* A field is worth showing whenever its new value exists and
                   either there's no old value to compare against (a pure
                   "recorded this" entry, e.g. Room Added) or the value
                   actually changed — previously this required *both*
                   old_value and new_value, which silently hid every
                   new-value-only entry (Room Added among them). */
                const newFields = entry.new_value
                  ? Object.keys(entry.new_value).filter(
                      (k) => !entry.old_value || entry.old_value[k] !== entry.new_value[k],
                    )
                  : [];
                return (
                  <div key={entry.id} style={{ fontSize: "0.82rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <Label basic>{AUDIT_ACTION_LABELS[entry.action] || entry.action}</Label>
                      <span style={{ opacity: 0.6 }}>{entry.changed_by_name}</span>
                      {entry.changed_at && (
                        <span style={{ opacity: 0.6 }}>
                          {fmtDateTime(entry.changed_at)}
                        </span>
                      )}
                    </div>
                    {newFields.length > 0 && (
                      <div style={{ marginTop: 4, opacity: 0.8 }}>
                        {newFields.map((k) => {
                          const hasOld =
                            entry.old_value &&
                            Object.prototype.hasOwnProperty.call(entry.old_value, k) &&
                            entry.old_value[k] != null;
                          return (
                            <div key={k}>
                              {formatAuditFieldLabel(k)}:{" "}
                              {hasOld
                                ? `${formatAuditFieldValue(entry.old_value[k], k)} → ${formatAuditFieldValue(entry.new_value[k], k)}`
                                : formatAuditFieldValue(entry.new_value[k], k)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {entry.remarks && (
                      <div style={{ marginTop: 4, fontStyle: "italic" }}>{entry.remarks}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── §10 Emails Sent ──
           Every row here is a message the mail service accepted. A send that
           failed leaves nothing behind, so this reads as what the applicant
           actually received, not what was attempted. ── */}
        <Divider />
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={toggleMailHistory}
        >
          <Header as="h5" icon="mail" content="Emails Sent" style={{ marginBottom: 0 }} />
          <Icon name={mailHistoryOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
        </div>
        {mailHistoryOpen && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            {mailLogLoading ? (
              <span style={{ fontSize: "0.82rem", opacity: 0.6 }}>Loading…</span>
            ) : mailLog.length === 0 ? (
              <span style={{ fontSize: "0.82rem", opacity: 0.6 }}>No emails sent yet.</span>
            ) : (
              mailLog.map((entry) => {
                const expanded = expandedMailId === entry.id;
                return (
                  <div
                    key={entry.id}
                    style={{ fontSize: "0.82rem", cursor: "pointer" }}
                    onClick={() => setExpandedMailId(expanded ? null : entry.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Label basic size="tiny">{entry.templateLabel}</Label>
                      <span style={{ color: "rgba(0,0,0,0.45)" }}>
                        {entry.sentByName} · {fmtDateTime(entry.sentAt)}
                      </span>
                      {entry.amount && (
                        <Label basic size="tiny" color="orange">₹ {entry.amount}</Label>
                      )}
                      <Icon name={expanded ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
                    </div>
                    <div style={{ marginTop: 2, color: "rgba(0,0,0,0.6)" }}>{entry.subject}</div>
                    {expanded && (
                      <Segment style={{ marginTop: 6 }}>
                        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>
                          {entry.body}
                        </pre>
                      </Segment>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

      </Segment>

      {/* ── Rail: Status + Key Facts, Room Allocation, and the primary
         actions — kept together and sticky so the actual admin task never
         requires scrolling past the read-only sections on the left. ── */}
      <div className="bhu-detail-rail">
        <Segment className="bhu-rail-card">
          <StatusBadge status={booking.status} />
          {booking.createdAt && (
            <div style={{ fontSize: "0.74rem", color: "rgba(0,0,0,0.45)", marginTop: 6 }}>
              Submitted {fmtDateTime(booking.createdAt)}
            </div>
          )}
          {lastTransfer && (
            <Label basic color="orange" size="small" style={{ marginTop: 8 }}>
              <Icon name="exchange" />
              Transferred request: {lastTransfer.from_gh_name || "—"} → {lastTransfer.to_gh_name || "—"}
              {lastTransfer.transferred_at && (
                <Label.Detail>{fmtDateTime(lastTransfer.transferred_at)}</Label.Detail>
              )}
            </Label>
          )}
          <div className="bhu-stat-grid">
            <Stat
              icon="calendar alternate outline"
              label="Nights"
              value={sDays || "—"}
              sub={`${fmtDate(booking.checkIn)} → ${fmtDate(booking.checkOut)}`}
            />
            <Stat icon="door open" label="Rooms" value={gRoomCount || "—"} sub="requested" />
            <Stat
              icon="user outline"
              label="Guests"
              value={gGuestCount}
              sub={gCount ? `1 + ${gCount} accompanying` : "primary guest only"}
            />
            {/* The house(s) the rooms actually came from, not the one the
                indent was filed with — otherwise this stat contradicts the
                room list below it whenever a room is given from elsewhere. */}
            <Stat
              icon="building outline"
              label="Guest House"
              value={
                (hasAssignedRoom && booking.stayGuestHouseNames?.join(", ")) ||
                (lastTransfer && booking.guestHouseName) ||
                prefNames[0] ||
                "—"
              }
              sub={hasAssignedRoom ? "assigned" : lastTransfer ? "transferred here" : "top preference"}
            />
          </div>

          {/* Raw field overrides — only for has_edit_access admins. These have
              no read-only <F> row of their own on the left, so they live here.
              Each edit is written straight through with no side effects. */}
          {canFieldEdit && (
            <>
              <Divider />
              <div style={{ fontSize: "0.64rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "rgba(0,0,0,0.45)", marginBottom: 6 }}>
                Admin field overrides
              </div>
              <Grid columns={1} stackable>
                <EditableF
                  label="Status"
                  value={<StatusBadge status={booking.status} />}
                  editable
                  onSave={saveField}
                  fieldKey="status"
                  type="select"
                  options={FIELD_STATUS_OPTIONS}
                  draftValue={apiStatus}
                  warning="Sets the status directly — no rooms are assigned or released, no email is sent, and no status-history row is written."
                />
                <EditableF
                  label="Assigned Guest House"
                  value={
                    guestHouses.find((g) => g.id === booking.guestHouseId)?.name || "—"
                  }
                  editable
                  onSave={saveField}
                  fieldKey="assigned_gh"
                  type="select"
                  options={assignedGhOptions}
                  draftValue={booking.guestHouseId}
                  warning="Moves the indent's owning queue directly — no transfer record is logged and rooms are not reassigned."
                />
              </Grid>
            </>
          )}
        </Segment>

        {/* ── Remarks ──
            A free-form internal scratchpad for whoever is handling the
            request. Append-only: an entry is never edited or deleted, so the
            list below the box is the complete history. Admin-only — the
            applicant never sees it. */}
        <Segment className="bhu-rail-card">
          {/* Same click-the-header-to-toggle pattern as Status History and
             Activity History on the left. */}
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => setRemarksOpen((o) => !o)}
          >
            <Header as="h5" icon="sticky note outline" content="Remarks" style={{ marginBottom: 0 }} />
            {remarkList.length > 0 && (
              <span style={{ fontSize: "0.74rem", color: "rgba(0,0,0,0.45)", marginLeft: 6 }}>
                {remarkList.length}
              </span>
            )}
            <Icon name={remarksOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
          </div>

          {remarksOpen ? (
            <>
              <Form style={{ marginTop: 10 }}>
                <Form.TextArea
                  rows={2}
                  placeholder="Add a remark…"
                  value={remarkDraft}
                  onChange={(e) => setRemarkDraft(e.target.value)}
                />
              </Form>
              {/* Semantic buttons carry no top margin, so without this the
                 button butts straight up against the textarea's bottom edge. */}
              <Button
                primary
                size="mini"
                icon="plus"
                content="Add"
                loading={remarkSubmitting}
                disabled={remarkSubmitting || !remarkDraft.trim()}
                onClick={handleAddRemark}
                type="button"
                style={{ marginTop: 10 }}
              />
              {remarkError && (
                <Message negative size="mini" content={remarkError} style={{ marginTop: 10 }} />
              )}
              {/* Nothing at all until there's a remark to show. Capped so a
                 long-running thread scrolls inside the card instead of pushing
                 Room Allocation and the action buttons off the rail. */}
              {remarkList.length > 0 && (
                <div className="bhu-remark-list">{remarkList.map(renderRemark)}</div>
              )}
            </>
          ) : (
            /* Collapsed: just the newest remark, so the rail still says at a
               glance whether anything has been noted. remarkList is newest-first. */
            remarkList.length > 0 && (
              <div style={{ marginTop: 10 }}>{renderRemark(remarkList[0])}</div>
            )
          )}
        </Segment>

        {/* ── Room Allocation Panel (actionable bookings only) ──
            Guest House dropdown picks which house's rooms the Room
            Number(s) dropdown below is browsing — switching it no longer
            clears earlier picks, so an admin can build a selection spanning
            several houses. */}
        {isActionable && (
          <Segment className="bhu-rail-card">
            <Header as="h5" icon="key" content="Room Allocation" />
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                <Icon name="building outline" />
                Guest House
              </label>
              <Dropdown
                selection
                search
                fluid
                placeholder="— Select Guest House —"
                options={guestHouses.map((gh) => ({
                  key: gh.id,
                  value: gh.id,
                  text: gh.name,
                }))}
                value={viewGhId || ""}
                onChange={(_, data) => setViewGhId(data.value ? Number(data.value) : null)}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                <Icon name="door open" />
                Room Number(s){" "}
                <span style={{ fontWeight: 400, opacity: 0.6 }}>
                  {selectedRoomIds.length}/{requiredRoomCount} selected
                </span>
              </label>
              <Dropdown
                multiple
                selection
                search
                fluid
                className="bhu-room-select"
                loading={availabilityLoading}
                disabled={!viewGhId || availabilityLoading}
                placeholder={
                  !viewGhId
                    ? "Select guest house first"
                    : availabilityLoading
                      ? "Checking availability…"
                      : `Select up to ${requiredRoomCount} room(s) — ${viewGhRooms.length} vacant`
                }
                options={roomDropdownOptions.map((r) => ({
                  key: r.id,
                  value: r.id,
                  disabled:
                    selectedRoomIds.length >= requiredRoomCount &&
                    !selectedRoomIds.includes(r.id),
                  text: roomOptionText(r),
                }))}
                value={selectedRoomIds}
                onChange={(_, data) => {
                  const ids = data.value.map(Number);
                  setSelectedRoomIds(ids.slice(0, requiredRoomCount));
                }}
              />
            </div>
            {selectedRooms.length > 0 && (
              <Message info style={{ marginTop: 12, backgroundColor: "#d9f1de" }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  <Icon name="check circle" />
                  {selectedRooms.length} room{selectedRooms.length > 1 ? "s" : ""} selected
                </div>
                {selectedRooms.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ", "}Room <strong>{r.roomNumber}</strong> · {r.roomType} ({r.guestHouseName})
                  </span>
                ))}
                {/* Worth saying once, plainly: the allotment goes through, but
                    check-in will refuse the room until housekeeping releases
                    it. Harmless for a stay weeks out, which is the usual case
                    — a post-checkout hold never expires on its own unless the
                    house has auto-release on. */}
                {selectedRooms.some((r) => r.underMaintenance) && (
                  <div style={{ marginTop: 6, fontWeight: 600 }}>
                    <Icon name="wrench" />
                    Room
                    {selectedRooms.filter((r) => r.underMaintenance).length > 1 ? "s " : " "}
                    {selectedRooms
                      .filter((r) => r.underMaintenance)
                      .map((r) => r.roomNumber)
                      .join(", ")}{" "}
                    under maintenance — mark available before the guest checks in.
                  </div>
                )}
              </Message>
            )}
            <RoomWindowsPanel
              rooms={selectedRooms}
              drafts={confirmRoomWindows}
              onChange={setConfirmRoomWindows}
            />
          </Segment>
        )}

        {/* ── Add Room Panel — extra rooms beyond the original allocation,
            available once the booking already holds a room. Rooms can be
            picked from any guest house the admin is authorized for. ── */}
        {canAddRoom && onAddRoom && (
          <Segment className="bhu-rail-card">
            <Header as="h5" icon="add square outline" content="Add Room" />
            <AddRoomWindowFields
              booking={booking}
              value={addRoomWin}
              onChange={setAddRoomWin}
              hiddenRooms={addRoomHidden}
            />
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                <Icon name="building outline" />
                Guest House
              </label>
              <Dropdown
                selection
                search
                fluid
                placeholder="— Select Guest House —"
                options={guestHouses.map((gh) => ({
                  key: gh.id,
                  value: gh.id,
                  text: gh.name,
                }))}
                value={addRoomViewGhId || ""}
                onChange={(_, data) => setAddRoomViewGhId(data.value ? Number(data.value) : null)}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                <Icon name="door open" />
                Room Number(s)
              </label>
              <Dropdown
                multiple
                selection
                search
                fluid
                loading={addRoomVacancyLoading}
                disabled={!addRoomViewGhId || addRoomVacancyLoading}
                placeholder={
                  !addRoomViewGhId
                    ? "Select guest house first"
                    : addRoomVacancyLoading
                      ? "Checking availability…"
                      : `Select room(s) — ${addRoomViewGhRooms.length} vacant`
                }
                options={addRoomDropdownOptions.map((r) => ({
                  key: r.id,
                  value: r.id,
                  text: roomOptionText(r),
                }))}
                value={addRoomIds}
                onChange={(_, data) => setAddRoomIds(data.value.map(Number))}
              />
            </div>
            {addRoomSelectedRooms.length > 0 && (
              <Message info style={{ marginTop: 12, backgroundColor: "#d9f1de" }}>
                <Icon name="check circle" />
                {addRoomSelectedRooms.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ", "}Room <strong>{r.roomNumber}</strong> · {r.roomType} ({r.guestHouseName})
                  </span>
                ))}
              </Message>
            )}
            <RoomWindowsPanel
              rooms={addRoomSelectedRooms}
              drafts={addRoomWindows}
              onChange={setAddRoomWindows}
            />
            <Button
              color="green"
              fluid
              style={{ marginTop: 12 }}
              disabled={!addRoomIds.length || !isAddRoomWindowValid(addRoomWin)}
              loading={addRoomSubmitting}
              onClick={handleAddRoomAction}
              type="button"
            >
              <Icon name="add square outline" />
              {addRoomIds.length > 1 ? `Add ${addRoomIds.length} Rooms` : "Add Room"}
            </Button>
          </Segment>
        )}

        <BillCard booking={booking} onOpenBill={onOpenBill} />

        {/* ── Actions / status footer ── */}
        <Segment className="bhu-rail-card">
          {isConfirmed && (
            <p style={{ margin: 0, fontSize: "0.85rem", color: "rgba(0,0,0,0.6)" }}>
              This booking has been approved. No further action required.
            </p>
          )}
          {!isActionable && !isConfirmed && (
            <p style={{ margin: 0, fontSize: "0.85rem", color: "rgba(0,0,0,0.6)" }}>
              This booking has been {booking.status?.toLowerCase()}.
            </p>
          )}
          {isActionable && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button
                fluid
                color="green"
                disabled={!canApprove}
                onClick={() => openConfirm("confirm")}
                type="button"
              >
                <Icon name="check circle outline" />
                Confirm{selectedRoomIds.length > 0 ? ` (${selectedRoomIds.length} Room${selectedRoomIds.length > 1 ? "s" : ""})` : ""}
              </Button>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  style={{ flex: 1 }}
                  color="blue"
                  onClick={() => {
                    setTransferGhId("");
                    setTransferRemarks("");
                    setTransferOpen(true);
                  }}
                  type="button"
                >
                  <Icon name="exchange" />
                  Transfer
                </Button>
                <InfoHint content={REJECT_HINT}>
                  <Button
                    style={{ flex: 1 }}
                    color="red"
                    basic
                    onClick={() => {
                      setRejectRemarks("");
                      setRejectOpen(true);
                    }}
                    type="button"
                  >
                    <Icon name="times circle outline" />
                    Reject
                    <Icon name="info circle" style={{ marginLeft: 6, opacity: 0.6 }} />
                  </Button>
                </InfoHint>
              </div>
              {canCancel && (
                <InfoHint content={CANCEL_HINT}>
                  <Button
                    fluid
                    color="red"
                    onClick={() => {
                      setCancelRemarks("");
                      setCancelOpen(true);
                    }}
                    type="button"
                  >
                    <Icon name="ban" />
                    Cancel
                    <Icon name="info circle" style={{ marginLeft: 6, opacity: 0.6 }} />
                  </Button>
                </InfoHint>
              )}
            </div>
          )}
          {/* Cancel is also available for confirmed bookings */}
          {isConfirmed && canCancel && (
            <InfoHint content={CANCEL_HINT}>
              <Button
                fluid
                color="red"
                style={{ marginTop: 10 }}
                onClick={() => {
                  setCancelRemarks("");
                  setCancelOpen(true);
                }}
                type="button"
              >
                <Icon name="ban" />
                Cancel Booking
                <Icon name="info circle" style={{ marginLeft: 6, opacity: 0.6 }} />
              </Button>
            </InfoHint>
          )}
          {(canReschedule || canEditDetails) && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {canReschedule && (
                <Button
                  style={{ flex: 1 }}
                  basic
                  color="blue"
                  onClick={() => {
                    setRescheduleForm({
                      arrivalDate: booking.checkIn || "",
                      arrivalTime: booking.arrivalTime || "",
                      departureDate: booking.checkOut || "",
                      departureTime: booking.departureTime || "",
                      remarks: "",
                    });
                    setRescheduleError("");
                    setRescheduleOpen(true);
                  }}
                  type="button"
                >
                  <Icon name="calendar alternate outline" />
                  Reschedule
                </Button>
              )}
              {canEditDetails && (
                <Button
                  style={{ flex: 1 }}
                  basic
                  onClick={() => {
                    setEditForm({
                      employeeId: apEmpId, applicantName: apName, designation: apDesig,
                      department: apDept, email: apEmail, mobile: apMobile,
                      guestName: gName, guestDesignation: gDesig, guestMobile: gMobile,
                      guestAddress: gAddr, accompanyingPersonsCount: String(gCount || 0),
                      roomsRequired: booking.roomCount || "",
                      paymentResponsibility: booking.paymentResponsibility || "",
                      paymentResponsibilityOther: booking.paymentResponsibilityOther || "",
                    });
                    setEditPrefs(seedEditPrefs());
                    setPrefPanelOpen({});
                    setEditAttachmentFile(null);
                    setEditDetailsError("");
                    setEditDetailsOpen(true);
                  }}
                  type="button"
                >
                  <Icon name="edit outline" />
                  Edit Details
                </Button>
              )}
            </div>
          )}

          {/* Mailing is not tied to a status: an advance request goes out while
             rooms are only blocked, and a confirmation for a non-official
             booking waits on the payment landing. So this stays available
             throughout, unlike the approval actions above. */}
          {onSendMail && (
            <Button
              fluid
              basic
              color="teal"
              style={{ marginTop: 8 }}
              onClick={() => setSendMailOpen(true)}
              type="button"
            >
              <Icon name="mail outline" />
              Send Mail
            </Button>
          )}
        </Segment>
      </div>
      </div>

      {/* ── Send Mail Dialog ── */}
      <SendMailModal
        open={sendMailOpen}
        bookingId={booking.id}
        empId={apEmpId}
        onSend={onSendMail}
        onClose={() => setSendMailOpen(false)}
        onSent={handleMailSent}
      />

      {/* ── Confirm / Partial Confirm Dialog ── */}
      {confirmOpen && (
        <Modal
          open
          size="small"
          onClose={() => setConfirmOpen(false)}
        >
          <Modal.Header>
            <Icon
              name={
                actionMode === "partial" ? "adjust" : "question circle outline"
              }
            />
            {actionMode === "partial"
              ? "Partially Confirm Booking"
              : "Confirm Booking"}
          </Modal.Header>
          <Modal.Content style={{ paddingTop: 12, paddingBottom: 12 }}>
            <p style={{ marginBottom: 8 }}>
              {actionMode === "partial"
                ? "Partially confirm with the selected rooms?"
                : "Confirm this booking?"}
            </p>
            {selectedRooms.length > 0 && (
              <Message info style={{ padding: "10px 14px", marginBottom: 8, backgroundColor: "#d9f1de" }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {selectedRooms.length} room{selectedRooms.length > 1 ? "s" : ""}
                </div>
                {selectedRooms.map((r) => (
                  <span key={r.id} style={{ display: "block" }}>
                    <Icon name="key" />
                    Room <strong>{r.roomNumber}</strong> — {r.roomType} ({r.guestHouseName})
                  </span>
                ))}
              </Message>
            )}
            <Form>
              <Form.TextArea
                placeholder={
                  actionMode === "partial"
                    ? "Note (e.g. only 1 room available instead of 2 requested)"
                    : "Remarks (optional)"
                }
                rows={1}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={() => setConfirmOpen(false)} type="button" disabled={actionSubmitting}>
              Cancel
            </Button>
            <Button
              color={actionMode === "partial" ? "violet" : "green"}
              onClick={handleConfirmAction}
              loading={actionSubmitting}
              disabled={actionSubmitting}
              type="button"
            >
              <Icon name="check" />
              {actionMode === "partial"
                ? "Yes, Partial Confirm"
                : "Yes, Confirm"}
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Reject Dialog ── */}
      {rejectOpen && (
        <Modal
          open
          size="mini"
          onClose={() => setRejectOpen(false)}
        >
          <Modal.Header>
            <Icon name="times circle outline" />
            Reject Booking
          </Modal.Header>
          <Modal.Content>
            <p>
              Reject booking <strong>{booking.bookingCode}</strong>?
            </p>
            <Form>
              <Form.TextArea
                placeholder="Reason for rejection (optional)"
                rows={3}
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={() => setRejectOpen(false)} type="button" disabled={actionSubmitting}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleRejectAction}
              loading={actionSubmitting}
              disabled={actionSubmitting}
              type="button"
            >
              <Icon name="times" />
              Yes, Reject
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Transfer Dialog ── */}
      {transferOpen && (
        <Modal
          open
          size="small"
          onClose={() => setTransferOpen(false)}
        >
          <Modal.Header>
            <Icon name="exchange" />
            Transfer Booking
          </Modal.Header>
          <Modal.Content>
            <p>
              Transfer <strong>{booking.bookingCode}</strong> to another guest
              house.
            </p>
            <Form>
              <Form.Field>
                <label>Transfer To</label>
                <Dropdown
                  fluid
                  selection
                  search
                  placeholder={
                    transferOptions.length
                      ? "— Select Guest House —"
                      : "No other guest house available to transfer to"
                  }
                  disabled={!transferOptions.length}
                  options={transferOptions.map((g) => ({
                    key: g.id,
                    value: g.id,
                    text: g.name,
                  }))}
                  value={transferGhId || ""}
                  onChange={(_, data) => setTransferGhId(data.value)}
                />
              </Form.Field>
              <Form.TextArea
                placeholder="Reason for transfer (optional)"
                rows={3}
                value={transferRemarks}
                onChange={(e) => setTransferRemarks(e.target.value)}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={() => setTransferOpen(false)} type="button" disabled={actionSubmitting}>
              Cancel
            </Button>
            <Button
              color="blue"
              disabled={!transferGhId || actionSubmitting}
              loading={actionSubmitting}
              onClick={handleTransferAction}
              type="button"
            >
              <Icon name="exchange" />
              Transfer
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Cancel Dialog ── */}
      {cancelOpen && (
        <Modal
          open
          size="mini"
          onClose={() => setCancelOpen(false)}
        >
          <Modal.Header>
            <Icon name="ban" />
            Cancel Booking
          </Modal.Header>
          <Modal.Content>
            <p>
              Cancel booking <strong>{booking.bookingCode}</strong>? This
              cannot be undone.
            </p>
            <Form>
              <Form.TextArea
                placeholder="Reason for cancellation (optional)"
                rows={3}
                value={cancelRemarks}
                onChange={(e) => setCancelRemarks(e.target.value)}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={() => setCancelOpen(false)} type="button" disabled={actionSubmitting}>
              Back
            </Button>
            <Button
              color="red"
              onClick={handleCancelAction}
              loading={actionSubmitting}
              disabled={actionSubmitting}
              type="button"
            >
              <Icon name="ban" />
              Yes, Cancel
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Reschedule Dialog ── */}
      {rescheduleOpen && (
        <Modal open size="small" onClose={() => setRescheduleOpen(false)}>
          <Modal.Header>
            <Icon name="calendar alternate outline" />
            {/* Nothing is allotted yet on a pending booking, so this is an
                amendment to the request rather than a true reschedule. */}
            {isPending ? "Change Requested Dates" : "Reschedule Booking"}
          </Modal.Header>
          <Modal.Content>
            {rescheduleError && <Message negative content={rescheduleError} />}
            {isPending && (
              <Message
                info
                size="small"
                content="This booking has no rooms allotted yet, so the new dates are applied to the request directly. Availability is checked when it's confirmed."
              />
            )}
            <Form>
              <Form.Group widths="equal">
                <Form.Input
                  label="Arrival Date"
                  type="date"
                  value={rescheduleForm.arrivalDate}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, arrivalDate: e.target.value }))}
                />
                <TimeInput12h
                  label="Arrival Time"
                  value={rescheduleForm.arrivalTime}
                  onChange={(v) => setRescheduleForm((f) => ({ ...f, arrivalTime: v }))}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  label="Departure Date"
                  type="date"
                  min={rescheduleForm.arrivalDate || undefined}
                  value={rescheduleForm.departureDate}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, departureDate: e.target.value }))}
                />
                <TimeInput12h
                  label="Departure Time"
                  value={rescheduleForm.departureTime}
                  onChange={(v) => setRescheduleForm((f) => ({ ...f, departureTime: v }))}
                />
              </Form.Group>
              <Form.TextArea
                label="Remarks (optional)"
                rows={2}
                value={rescheduleForm.remarks}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, remarks: e.target.value }))}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={() => setRescheduleOpen(false)} type="button" disabled={actionSubmitting}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleRescheduleAction}
              loading={actionSubmitting}
              disabled={actionSubmitting}
              type="button"
            >
              <Icon name="save" />
              Save
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Edit Applicant/Guest Details Dialog ── */}
      {editDetailsOpen && (
        <Modal open size="large" onClose={() => setEditDetailsOpen(false)}>
          <Modal.Header>
            <Icon name="edit outline" />
            Edit Booking Details
          </Modal.Header>
          <Modal.Content>
            {editDetailsError && <Message negative content={editDetailsError} />}
            <Form>
              <Header as="h5" content="Applicant" />
              <Form.Group widths="equal">
                <Form.Input
                  label="Employee ID"
                  value={editForm.employeeId || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, employeeId: e.target.value }))}
                />
                <Form.Input
                  label="Applicant Name"
                  value={editForm.applicantName || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, applicantName: e.target.value }))}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  label="Designation"
                  value={editForm.designation || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, designation: e.target.value }))}
                />
                <Form.Input
                  label="Department"
                  value={editForm.department || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, department: e.target.value }))}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  label="Email"
                  value={editForm.email || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                />
                <Form.Input
                  label="Mobile"
                  value={editForm.mobile || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, mobile: e.target.value }))}
                />
              </Form.Group>
              <Divider />
              <Header as="h5" content="Guest" />
              <Form.Group widths="equal">
                <Form.Input
                  label="Guest Name"
                  value={editForm.guestName || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, guestName: e.target.value }))}
                />
                <Form.Input
                  label="Guest Designation"
                  value={editForm.guestDesignation || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, guestDesignation: e.target.value }))}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  label="Guest Mobile"
                  value={editForm.guestMobile || ""}
                  onChange={(e) => setEditForm((f) => ({ ...f, guestMobile: e.target.value }))}
                />
                <Form.Input
                  label="Accompanying Persons"
                  type="number"
                  min="0"
                  value={editForm.accompanyingPersonsCount || "0"}
                  onChange={(e) => setEditForm((f) => ({ ...f, accompanyingPersonsCount: e.target.value }))}
                />
              </Form.Group>
              <Form.TextArea
                label="Guest Address"
                rows={2}
                value={editForm.guestAddress || ""}
                onChange={(e) => setEditForm((f) => ({ ...f, guestAddress: e.target.value }))}
              />

              <Divider />
              <Header as="h5" content="Guest House Preferences & Payment" />

              {canEditPreferences ? (
                <>
                <div style={{ maxWidth: 420, marginBottom: 18 }}>
                  <Form.Input
                    label="Rooms Required"
                    type="number"
                    min="1"
                    value={editForm.roomsRequired || ""}
                    onChange={(e) => setEditForm((f) => ({ ...f, roomsRequired: e.target.value }))}
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                    gap: 18,
                  }}
                >
                {editPrefs.map((pref, idx) => {
                  const ghTypes = editRoomTypesByGh[pref.guestHouseId];
                  const isPanelOpen = Boolean(prefPanelOpen[idx]);
                  const chosen = (pref.roomRequests || []).filter((rr) => Number(rr.count) > 0);
                  const summary = chosen
                    .map((rr) => `${rr.count} ${rr.roomTypeName || "Room"}`)
                    .join(", ");
                  const prefTotal = chosen.reduce((sum, rr) => sum + Number(rr.count), 0);
                  return (
                    <div key={idx} style={{ marginBottom: 18 }}>
                      <Form.Select
                        clearable
                        search
                        label={`Preference ${idx + 1}`}
                        placeholder="Select Guest House"
                        options={prefGhOptions}
                        value={pref.guestHouseId}
                        onChange={(_, data) => setPrefGuestHouse(idx, data.value)}
                      />

                      {Boolean(pref.guestHouseId) && (
                        <div style={{ marginTop: 10 }}>
                          <div
                            style={{
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              color: "rgba(0,0,0,0.5)",
                              marginBottom: 6,
                            }}
                          >
                            Room types
                          </div>

                          <div
                            style={{
                              border: "1px solid rgba(34,36,38,.15)",
                              borderRadius: 8,
                              overflow: "hidden",
                              background: "#fff",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setPrefPanelOpen((prev) => ({ ...prev, [idx]: !prev[idx] }))
                              }
                              style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px 16px",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "1rem",
                                color: "#143a5a",
                                textAlign: "left",
                              }}
                            >
                              <span>{summary || "Select room types"}</span>
                              <Icon name={isPanelOpen ? "chevron up" : "chevron down"} />
                            </button>

                            {isPanelOpen && (
                              <div style={{ borderTop: "1px solid rgba(34,36,38,.1)" }}>
                                {ghTypes === undefined ? (
                                  <p style={{ opacity: 0.6, padding: "12px 16px", margin: 0 }}>
                                    <Icon name="spinner" loading /> Loading room types…
                                  </p>
                                ) : ghTypes.length === 0 ? (
                                  <p style={{ opacity: 0.6, padding: "12px 16px", margin: 0 }}>
                                    <em>No room types configured for this guest house.</em>
                                  </p>
                                ) : (
                                  ghTypes.map((roomType, typeIdx) => {
                                    const count =
                                      Number(
                                        (pref.roomRequests || []).find(
                                          (rr) => Number(rr.roomTypeId) === Number(roomType.id),
                                        )?.count,
                                      ) || 0;
                                    return (
                                      <div
                                        key={roomType.id}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: 12,
                                          padding: "14px 16px",
                                          borderTop: typeIdx > 0 ? "1px solid rgba(34,36,38,.08)" : "none",
                                        }}
                                      >
                                        <div>
                                          <div style={{ fontWeight: 700 }}>{roomType.name}</div>
                                          <div style={{ fontSize: "0.82rem", color: "rgba(0,0,0,0.55)" }}>
                                            Capacity: {roomType.capacity} guest
                                            {roomType.capacity === 1 ? "" : "s"}
                                            {roomType.base_price ? ` · ₹${roomType.base_price}/night` : ""}
                                          </div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                          <Button
                                            circular
                                            basic
                                            icon="minus"
                                            size="mini"
                                            type="button"
                                            disabled={count <= 0}
                                            aria-label={`Decrease ${roomType.name} count`}
                                            onClick={() => stepPrefRoomCount(idx, roomType, -1)}
                                          />
                                          <span style={{ minWidth: 18, textAlign: "center" }}>{count}</span>
                                          <Button
                                            circular
                                            color="blue"
                                            icon="plus"
                                            size="mini"
                                            type="button"
                                            disabled={prefTotal >= editRoomsRequired}
                                            aria-label={`Increase ${roomType.name} count`}
                                            onClick={() => stepPrefRoomCount(idx, roomType, 1)}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>

                          {Boolean(editRoomsRequired) && prefTotal !== editRoomsRequired && (
                            <div style={{ color: "#db2828", fontSize: "0.8rem", marginTop: 6 }}>
                              Selected rooms ({prefTotal}) must equal Rooms Required ({editRoomsRequired}).
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
                </>
              ) : (
                <Message
                  info
                  size="small"
                  content="Guest house preferences and the rooms count can only be changed while the booking is still pending — rooms have already been allotted for this one."
                />
              )}

              <div style={{ maxWidth: 420, marginTop: 18 }}>
                <Form.Select
                  label="Payment Mode"
                  placeholder="Select payment responsibility"
                  options={paymentDropdownOptions}
                  value={editForm.paymentResponsibility || ""}
                  onChange={(_, data) => setEditForm((f) => ({
                    ...f,
                    paymentResponsibility: data.value,
                    ...(data.value !== OTHER_PAYMENT_CODE ? { paymentResponsibilityOther: "" } : {}),
                  }))}
                />
              </div>

              {editForm.paymentResponsibility === OTHER_PAYMENT_CODE && (
                <div style={{ maxWidth: 420, marginTop: 18 }}>
                  <Form.Input
                    label="Please Specify"
                    placeholder="Who is responsible for payment?"
                    value={editForm.paymentResponsibilityOther || ""}
                    onChange={(e) => setEditForm((f) => ({ ...f, paymentResponsibilityOther: e.target.value }))}
                  />
                </div>
              )}

              {booking.bookingType === "Official" && (
                <Form.Field style={{ maxWidth: 420, marginTop: 18 }}>
                  <label>Official Document</label>
                  {booking.officialAttachment && !editAttachmentFile && (
                    <div style={{ marginBottom: 8 }}>
                      <AttachmentPreview url={booking.officialAttachment} />
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 8 }}>
                    <Button
                      as="label"
                      htmlFor="edit-official-attachment-input"
                      basic
                      type="button"
                      style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
                    >
                      <Icon name="upload" />
                      {editAttachmentFile ? editAttachmentFile.name : "Replace file…"}
                    </Button>
                    {editAttachmentFile && (
                      <Button
                        basic
                        type="button"
                        icon="close"
                        content="Cancel"
                        style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
                        onClick={() => setEditAttachmentFile(null)}
                      />
                    )}
                  </div>
                  <input
                    id="edit-official-attachment-input"
                    type="file"
                    hidden
                    accept={OFFICIAL_ATTACHMENT_ACCEPT}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && file.size > MAX_ATTACHMENT_SIZE) {
                        e.target.value = "";
                        setEditDetailsError(ATTACHMENT_TOO_LARGE_MSG);
                        return;
                      }
                      setEditDetailsError("");
                      setEditAttachmentFile(file);
                    }}
                  />
                </Form.Field>
              )}
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={() => setEditDetailsOpen(false)} type="button" disabled={actionSubmitting}>
              Cancel
            </Button>
            <Button
              primary
              onClick={handleEditDetailsAction}
              loading={actionSubmitting}
              disabled={actionSubmitting}
              type="button"
            >
              <Icon name="save" />
              Save Changes
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Change Stay Window Dialog (per room) ── */}
      {roomActionOpen?.type === "extend" && (
        <Modal open size="tiny" onClose={() => setRoomActionOpen(null)}>
          <Modal.Header>
            <Icon name="calendar alternate outline" />
            Change Stay Window — Room {roomActionOpen.assignment.roomNumber}
          </Modal.Header>
          <Modal.Content>
            {roomActionError && <Message negative content={roomActionError} />}
            <p style={{ color: "rgba(0,0,0,0.6)" }}>
              This room is held{" "}
              {fmtDateAndTime(
                roomActionOpen.assignment.effectiveArrival || booking.checkIn,
                roomActionOpen.assignment.effectiveArrivalTime || booking.arrivalTime,
              )}
              {" — "}
              {fmtDateAndTime(
                roomActionOpen.assignment.effectiveDeparture || booking.checkOut,
                roomActionOpen.assignment.effectiveDepartureTime || booking.departureTime,
              )}
              . Move either end later or earlier — a departure must still be after the
              guest arrived or checked in. The room's window may sit inside the booking's
              or run past it.
            </p>
            <Form>
              {/* Pre-filled with the room's current arrival, same as departure
                  below -- so leaving it untouched still means "keep the
                  arrival it already has." Optional only in that it may be
                  cleared; the request still requires a departure. */}
              <Form.Group widths="equal">
                <Form.Input
                  label="New Arrival Date (optional)"
                  type="date"
                  value={extendArrivalDate}
                  onChange={(e) => setExtendArrivalDate(e.target.value)}
                />
                <TimeInput12h
                  label="New Arrival Time (optional)"
                  value={extendArrivalTime}
                  onChange={setExtendArrivalTime}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  label="New Departure Date"
                  type="date"
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                />
                <TimeInput12h label="New Departure Time" value={extendTime} onChange={setExtendTime} />
              </Form.Group>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              basic
              onClick={() => setRoomActionOpen(null)}
              type="button"
              disabled={roomActionSubmitting === `${roomActionOpen.assignment.id}:extend`}
            >
              Cancel
            </Button>
            <Button
              color="violet"
              onClick={handleRoomExtend}
              loading={roomActionSubmitting === `${roomActionOpen.assignment.id}:extend`}
              disabled={roomActionSubmitting === `${roomActionOpen.assignment.id}:extend` || !extendDate || !extendTime}
              type="button"
            >
              <Icon name="calendar alternate outline" />
              Change Stay Window
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {/* ── Change Room Dialog (per room) ── */}
      {roomActionOpen?.type === "removeRoom" && (
        <Modal open size="mini" onClose={() => setRoomActionOpen(null)}>
          <Modal.Header>
            <Icon name="trash alternate" />
            Remove Room {roomActionOpen.assignment.roomNumber}
          </Modal.Header>
          <Modal.Content>
            {roomActionError && <Message negative content={roomActionError} />}
            {booking.roomAssignments?.length === 1 && (
              <Message
                warning
                content="This is the only room on this booking — removing it will cancel the booking."
              />
            )}
            <p style={{ fontSize: "0.9rem" }}>
              Room {roomActionOpen.assignment.roomNumber} will be dropped from this booking and
              freed for other bookings. This cannot be undone.
            </p>
            <Form>
              <Form.TextArea
                label="Reason"
                required
                rows={2}
                placeholder="Why is this room being removed?"
                value={removeRoomRemarks}
                onChange={(e) => setRemoveRoomRemarks(e.target.value)}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              basic
              onClick={() => setRoomActionOpen(null)}
              type="button"
              disabled={roomActionSubmitting === `${roomActionOpen.assignment.id}:remove`}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleRoomRemove}
              loading={roomActionSubmitting === `${roomActionOpen.assignment.id}:remove`}
              disabled={
                roomActionSubmitting === `${roomActionOpen.assignment.id}:remove` ||
                !removeRoomRemarks.trim()
              }
              type="button"
            >
              <Icon name="trash alternate" />
              Remove Room
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      {roomActionOpen?.type === "changeRoom" && (() => {
        const a = roomActionOpen.assignment;
        const submittingKey = `${a.id}:changeRoom`;
        const viewGhRoomOptions = newRoomOptionsByGh.get(changeRoomViewGhId) || [];
        return (
          <Modal open size="small" onClose={() => setRoomActionOpen(null)}>
            <Modal.Header>
              <Icon name="exchange" />
              Change Room — currently Room {a.roomNumber}
            </Modal.Header>
            <Modal.Content>
              {roomActionError && <Message negative content={roomActionError} />}
              <Form>
                <Form.Field>
                  <label>New Room — Guest House</label>
                  <Dropdown
                    selection
                    search
                    fluid
                    placeholder="— Select Guest House —"
                    options={guestHouses.map((gh) => ({ key: gh.id, value: gh.id, text: gh.name }))}
                    value={changeRoomViewGhId || ""}
                    onChange={(_, data) => {
                      setChangeRoomViewGhId(data.value ? Number(data.value) : null);
                      setChangeRoomNewId("");
                    }}
                  />
                </Form.Field>
                <Form.Select
                  label="New Room"
                  search
                  loading={changeRoomVacancyLoading}
                  placeholder={
                    changeRoomVacancyLoading
                      ? "Checking availability…"
                      : viewGhRoomOptions.length
                        ? "— Select new room —"
                        : "No vacant rooms in this guest house"
                  }
                  disabled={changeRoomVacancyLoading || !viewGhRoomOptions.length}
                  options={viewGhRoomOptions.map((r) => ({
                    key: r.id,
                    value: r.id,
                    text: `Room ${r.roomNumber} (${r.roomType})`,
                  }))}
                  value={changeRoomNewId || ""}
                  onChange={(_, data) => setChangeRoomNewId(data.value)}
                />
              </Form>
            </Modal.Content>
            <Modal.Actions>
              <Button basic onClick={() => setRoomActionOpen(null)} type="button" disabled={roomActionSubmitting === submittingKey}>
                Cancel
              </Button>
              <Button
                color="blue"
                onClick={handleRoomChangeRoom}
                loading={roomActionSubmitting === submittingKey}
                disabled={!changeRoomNewId || roomActionSubmitting === submittingKey}
                type="button"
              >
                <Icon name="exchange" />
                Change Room
              </Button>
            </Modal.Actions>
          </Modal>
        );
      })()}
    </div>
  );
}
